/**
 * Capa de datos del dominio Pedidos/Operaciones (ADR-001).
 * Separa el acceso a datos de la UI. Las páginas consumen estas funciones.
 */
import { filtroDeFecha, type TipoDeFecha } from '@/lib/operations/filtroDeFecha';
import { lasQueYaVanConOtroTransportista, type EnvioDelLote } from '@/lib/operations/lasQueYaVanConOtroTransportista';
import { supabase } from '@/lib/supabase';
import { couriersDeSmartyDropPorNombre } from '@/lib/dropshipping/transportistasDeSmartyDrop';
import { configGeneralDelNegocio } from './configGeneralDelNegocio';
import { mensajeDeLaFuncion } from '@/lib/motivoDeLaFuncion';
import { logError } from '@/lib/logger';
import { marcarHito } from '@/lib/analytics/hitosDelArranque';
import { olvidarListasDePedidos, olvidarLoDeLaBilleteraDrop } from '@/lib/operations/olvidarLoBorrado';
import { ponerTransportistaDesdeElEnvio } from '@/lib/operations/transportistaDesdeElEnvio';
import { importFromSupplier, type SupplierImportResult } from '@/lib/operations/importarDelProveedor';
import { errorDeEdgeFunction } from '@/lib/errorDeEdgeFunction';
import { uploadAsset } from '@/lib/storage';
import { parseEdgeFunctionError } from '@/lib/edgeFunctionError';
import { elNumeroComoSeGuarda } from '@smarty/shared';
import { palabrasDeBusqueda, filtrarPorCanal, inferPaymentKind, defaultConfirmMode, type ConfirmMode, type OpsRole } from './orderMath';
import { filterAgenciesByUbigeo, type MatchedLevel } from '@/lib/agencyUbigeoFilter';
import { toTitle } from '@/components/logistics/agencyPicker';
import type { OrderRecord, OrderLifecycleStage, OrderTransitionEvent, OrderLine } from '@/types/operations.types';
import { esVenta } from './queues';
import { EMBEDS_DE_DROP, ponerLaParteDeQuienMira } from './elPedidoEnDrop';
import { escribirEnvio } from './elEnvioSeEscribe';

// Re-export para los consumidores que ya importan desde ordersApi.
export { defaultConfirmMode, type ConfirmMode };

// Columnas estables del listado (NO incluir columnas de migraciones aún no aplicadas
// — el listado es core y no debe acoplarse a O1/O2/O3). notes/tags van en el detalle.
const ORDER_COLUMNS =
  'id, tenant_id, order_number, status, lifecycle_stage, channel, source, attended_by, is_cod, contact_id, ' +
  'customer_name, customer_phone, customer_email, payment_status, shipping_status, billing_status, retenido, ' +
  'currency, subtotal, discount_total, shipping_total, tax_total, grand_total, ' +
  'amount_paid, amount_due, crm_lead_id, crm_deal_id, confirmed_at, created_at, fecha_efectiva, updated_at, source_metadata, sin_encargo_porque';

// Columnas del detalle (incluye notes/tags de O1 + vendedor/equipo). fetchOrder degrada si no están aplicadas.
// `sales_rep_id` es CLAVE para que el selector "Vendedor" muestre el vendedor migrado (catálogo Odoo).
const ORDER_DETAIL_COLUMNS = `${ORDER_COLUMNS}, notes, tags, salesperson_id, sales_rep_id, sales_team_id, shipping_cost, freight_payer`;

// Lista de Operaciones: core + comercial (salesperson_id) + perfil COD ampliado
// (agencia/tracking/estado-cobranza para Logística/Cobranzas) + 1ª línea (producto).
// salesperson_id vive en orders_v2 (O1 aplicada); el embed es 1 join (escala a la página, no al dataset).
const ORDER_COLUMNS_WITH_COD =
  `${ORDER_COLUMNS}, cancel_reason, salesperson_id, sales_rep_id, confirmed_by, almacen_tenant_id, courier_tenant_id, proveedor_tenant_id, shipping_cost, notes, cliente_contactado_en, ` +
  // El envío NO se lee del perfil: su verdad es `order_shipments` (embed de abajo). El perfil tenía
  // copias que divergían — `shalom_agency_name` quedó en 0 filas y `tracking_number` no lo consumía
  // nadie, mientras 211 pedidos ya tenían guía sólo en order_shipments. Ver ADR-011 §3 Fase 3.
  `cod_profile:order_delivery_profile(department, province, district, address, doc_number, doc_type, commitment_date, commitment_notes, collection_status, cod_collection_status, geo_lat, geo_lng, cobro_en_puerta, entregar_el), ` +
  `lines:order_lines(name, qty, display_type, created_at, product_id, sku, unit_price, catalogo:drop_catalogo(proveedor:drop_proveedores(nombre, contacto_wa))), ` +
  `shipments:order_shipments(id, carrier, agency_name, tracking_number, created_at, label_url, status, despachado_en, entregado_en, motorizado_nombre, motorizado_telefono), ` +
  // El encargo de Drop: el de este pedido y el de la venta (ver `elPedidoEnDrop.ts`).
  EMBEDS_DE_DROP +
  // Las visitas del que entrega (20260925167000): la fila Entrega dice la última.
  `intentos:order_intentos_de_entrega(resultado, reprogramado_para, created_at)`;

/** Columnas de orders_v2 por las que se permite ordenar la lista (allowlist anti-inyección). */
const SORTABLE_COLUMNS = new Set(['fecha_efectiva', 'created_at', 'amount_due', 'grand_total']);

export interface OrdersPageParams {
  tenantId: string;
  /**
   * Qué pedidos pedir, por COMPROMISO (`status`). El nombre sobrevive del modelo viejo y se
   * renombra en F6; el vocabulario ya es el nuevo — la base traduce con `fn_status_de` mientras
   * el panel de producción siga mandando el viejo. Ver `statusDePantalla`.
   */
  stages: string[];
  search?: string;
  paymentStatus?: string[];
  shippingStatus?: string[];
  channel?: string[];
  billingStatus?: string[];
  onlyWithDue?: boolean;
  /**
   * Sólo las ventas propias, no las de otros donde se tiene parte (despacho, entrega, producto). Lo pide
   * la pantalla cuando filtra por pago: el pago de una venta es de quien la vendió (ver `alcanceDeLaLista`).
   */
  soloLoPropio?: boolean;
  /** Filtra por quién saca la guía: `almacen` | `revendedor`. */
  registraLaGuia?: string[];
  /** `motorizado` (is_cod) | `agencia`. Con las dos, ninguna u otra (una vista vieja), no filtra. */
  modalidad?: string[];
  fechaTipo?: TipoDeFecha; fechaDesde?: string; fechaHasta?: string; // qué fecha y entre cuándo (`filtroDeFecha`)
  /**
   * Desde cuándo mirar (ISO). Es el MISMO período que las tarjetas de arriba.
   *
   * 🚨 SIN ESTO LA PANTALLA SE CONTRADECÍA. El selector gobernaba sólo los KPIs y la lista no tenía
   *    filtro de fecha: el founder vio «Saldo S/ 0,00» arriba y un pedido debiendo S/ 88 justo
   *    debajo. Los dos números estaban bien y medían cosas distintas — que es exactamente lo que
   *    hace imposible saber a cuál creerle.
   */
  createdFrom?: string;
  /** "<columna> <asc|desc>" — columna debe estar en SORTABLE_COLUMNS. */
  sort?: string;
  offset?: number;
  limit?: number;
}

/**
 * El techo de filas que PostgREST devuelve, pida lo que pida el cliente.
 *
 * 🚨 NO ES UNA PREFERENCIA NUESTRA, ES LA CONFIGURACIÓN DEL SERVIDOR (`max_rows` = 1000, medido
 *    contra el proyecto de producción). Pedir 1.501 filas devuelve 1.000 y NO avisa: no hay error,
 *    no hay bandera, la respuesta se ve completa.
 *
 * 🪤 ESE SILENCIO YA ERA UN BUG. La agrupación decidía trabajar en memoria cuando el conteo exacto
 *    daba ≤ 1.500, pero sólo recibía 1.000 filas — y sumaba esas, mostrando totales que el código
 *    llamaba «EXACTOS». Con 1.200 pedidos filtrados faltaban 200 y el número se veía normal.
 *
 * ⭐ Por eso vive acá y se exporta: cualquier decisión sobre «¿me entra todo en memoria?» tiene que
 *    leer ESTE número. Escribirlo a mano en otro archivo es cómo nació el problema.
 */
export const MAX_FILAS_POSTGREST = 1000;

export interface OrdersPage {
  items: OrderRecord[];
  /**
   * ¿Hay página siguiente? Sale de pedir UNA fila de más, no de contar el universo.
   *
   * 🚨 ANTES ESTO ERA UN `count: 'exact'` EN CADA PETICIÓN. Medido en producción: contar las 11.539
   *    ventas del tenant costaba 206 ms en frío, contra 0,37 ms de traer las 25 filas que se
   *    muestran. 560 veces el costo del dato — y la lista lo ESPERABA para poder pintarse.
   *
   * ⭐ Es exacto y gratis: si se piden 26 y vuelven 26, hay más. Es lo que hace Shopify.
   */
  hasNext: boolean;
  /**
   * El total, cuando alguien se tomó el trabajo de pedirlo aparte (`fetchOrdersTotal`).
   *
   * 🪤 `null` NO ES CERO: significa «todavía no se sabe». Quien lo pinte tiene que distinguirlos, o
   *    la pantalla va a decir «0 pedidos» mientras el conteo viaja.
   */
  total: number | null;
}

// ── Reporte agregado estilo Odoo (Group By + Measures + comparación) ──────────
export type OrdersReportGroupBy = 'date' | 'salesperson' | 'payment' | 'stage';
export type OrdersReportGranularity = 'day' | 'week' | 'month';
export interface OrdersReportMeasures { count: number; total: number; paid: number; due: number }
export interface OrdersReportGroup extends OrdersReportMeasures { key: string }
export interface OrdersReport {
  groups: OrdersReportGroup[];
  totals: OrdersReportMeasures;
  /** Medidas del periodo anterior por key (para Δ por grupo en dimensiones). */
  prevByKey: Record<string, OrdersReportMeasures>;
  /** Totales del periodo anterior (Δ general); null si no hay comparación. */
  prevTotals: OrdersReportMeasures | null;
}

/**
 * Reporte agregado server-side (RPC fn_orders_report): agrupa TODO el dataset por
 * fecha (día/semana/mes), vendedor, estado de pago o etapa, con subtotales y
 * comparación contra el periodo anterior equivalente. SECURITY INVOKER (RLS por tenant).
 */
export async function fetchOrdersReport(params: {
  tenantId: string;
  stages: string[];
  groupBy: OrdersReportGroupBy;
  granularity?: OrdersReportGranularity;
  dateFrom?: string | null;
  dateTo?: string | null;
  compare?: boolean;
}): Promise<OrdersReport> {
  const { data, error } = await supabase.rpc('fn_orders_report', {
    p_tenant: params.tenantId,
    p_stages: params.stages,
    p_group_by: params.groupBy,
    p_granularity: params.granularity ?? 'month',
    p_date_from: params.dateFrom ?? null,
    p_date_to: params.dateTo ?? null,
    p_compare: params.compare ?? false,
  });
  if (error) throw error;
  const r = (data ?? {}) as Record<string, unknown>;
  const toMeasures = (m: unknown): OrdersReportMeasures => {
    const x = (m ?? {}) as Record<string, unknown>;
    return { count: Number(x.count ?? 0), total: Number(x.total ?? 0), paid: Number(x.paid ?? 0), due: Number(x.due ?? 0) };
  };
  const groupsRaw = Array.isArray(r.groups) ? (r.groups as Array<Record<string, unknown>>) : [];
  const prevRaw = (r.prev_by_key ?? {}) as Record<string, unknown>;
  return {
    groups: groupsRaw.map((g) => ({ key: String(g.key), ...toMeasures(g) })),
    totals: toMeasures(r.totals),
    prevByKey: Object.fromEntries(Object.entries(prevRaw).map(([k, v]) => [k, toMeasures(v)])),
    prevTotals: r.prev_totals ? toMeasures(r.prev_totals) : null,
  };
}

export interface OrdersReportDetailRow {
  id: string;
  order_number: string;
  customer_name: string | null;
  customer_phone: string | null;
  sale_at: string;
  total: number;
  paid: number;
  due: number;
}

/**
 * Detalle (drill-down) de un grupo del reporte: los pedidos individuales del grupo,
 * con el MISMO bucketing que fn_orders_report (conteos consistentes con el subtotal).
 */
export async function fetchOrdersReportDetail(params: {
  tenantId: string;
  stages: string[];
  groupBy: OrdersReportGroupBy;
  granularity?: OrdersReportGranularity;
  groupKey: string;
  dateFrom?: string | null;
  dateTo?: string | null;
  limit?: number;
}): Promise<OrdersReportDetailRow[]> {
  const { data, error } = await supabase.rpc('fn_orders_report_detail', {
    p_tenant: params.tenantId,
    p_stages: params.stages,
    p_group_by: params.groupBy,
    p_granularity: params.granularity ?? 'month',
    p_group_key: params.groupKey,
    p_date_from: params.dateFrom ?? null,
    p_date_to: params.dateTo ?? null,
    p_limit: params.limit ?? 100,
  });
  if (error) throw error;
  const rows = Array.isArray(data) ? (data as Array<Record<string, unknown>>) : [];
  return rows.map((o) => ({
    id: String(o.id),
    order_number: String(o.order_number ?? ''),
    customer_name: (o.customer_name as string | null) ?? null,
    customer_phone: (o.customer_phone as string | null) ?? null,
    sale_at: String(o.sale_at ?? ''),
    total: Number(o.total ?? 0),
    paid: Number(o.paid ?? 0),
    due: Number(o.due ?? 0),
  }));
}

/**
 * Página de pedidos con filtros/búsqueda/orden/paginación resueltos EN LA BD
 * (como Shopify/Stripe). No trae el dataset completo al browser — escala a
 * tenants con cientos de miles de pedidos. `total` viene del count exacto.
 */
/**
 * Los filtros de la lista, en UN solo lugar.
 *
 * ⭐ EXISTE PORQUE AHORA HAY DOS CONSULTAS SOBRE EL MISMO CONJUNTO: la página (barata) y el total
 *    (cara, aparte). Si cada una armara sus filtros por su cuenta, el día que alguien agregue uno
 *    lo pondría en una sola — y la pantalla diría «página 3 de 12» sobre un universo distinto del
 *    que está mostrando. Nadie lo notaría hasta que los números no cerraran.
 *
 * 🪤 `soloContar` no es una optimización menor: pide `head` (cero filas) además del conteo. Sin
 *    eso, contar 11.539 pedidos también los TRAERÍA — el techo de PostgREST devolvería 1.000 filas
 *    completas que nadie lee, por una petición cuyo resultado útil es un solo número.
 */
function construirConsultaDePedidos(
  p: Omit<OrdersPageParams, 'offset' | 'limit' | 'sort'>,
  opciones?: { soloContar?: boolean },
) {
  const { tenantId, stages, search, paymentStatus, shippingStatus, channel, billingStatus,
    onlyWithDue, soloLoPropio, createdFrom, registraLaGuia, modalidad } = p;
  const fecha = filtroDeFecha(p);
  /*
   * 🪤 FILTRAR EXIGE `!inner`. Un embed normal trae el pedido AUNQUE no tenga fila en
   *    `drop_pedidos`; el `!inner` lo convierte en unión de verdad, que es lo que hace que filtrar
   *    por el campo del embed signifique algo. Sólo se arma así cuando hay filtro: en la vista sin
   *    filtro, un `!inner` escondería los pedidos sin encargo sin que nadie lo haya pedido.
   */
  const filtraPorRegistro = (registraLaGuia?.length ?? 0) > 0;
  const soloContar = opciones?.soloContar ?? false;
  const palabras = search ? palabrasDeBusqueda(search) : [];
  // Contar no necesita columnas. `id` es lo mínimo que PostgREST acepta y el `head` evita traerlas.
  const columnas = (soloContar
    ? (filtraPorRegistro ? 'id, interno:drop_pedidos!drop_pedidos_venta_id_fkey!inner(registro_lo_hace)' : 'id')
    : (filtraPorRegistro
      ? ORDER_COLUMNS_WITH_COD.replace('interno:drop_pedidos!drop_pedidos_venta_id_fkey', 'interno:drop_pedidos!drop_pedidos_venta_id_fkey!inner')
      : ORDER_COLUMNS_WITH_COD) + fecha.embebido)
    + (palabras.length ? `, ${EMBED_DE_BUSQUEDA}` : '');
  // ⭐ UNA LISTA PARA TODOS (actores §13): lo mío, lo que despacho, entrego o se vendió de mi producto; sin encargos internos.
  /*
   * 🐛 PERO LO QUE SE COBRA ES SÓLO LO MÍO (founder, 30-sep, mirando Vitalex). El saldo de una venta es lo
   *    que el cliente le debe a QUIEN VENDIÓ. Con el alcance de todos, Cobranzas de Vitalex listaba 14 ventas
   *    de drops (S/ 1.445) que ella no cobra —su parte le llega por la liquidación de Smarty— junto a sus 2
   *    propias (S/ 162). Los números de arriba ya sumaban sólo lo propio (`ops_orders_kpis_v2`).
   */
  const alcance = alcanceDeLaLista(tenantId, onlyWithDue || soloLoPropio);
  let query = supabase
    .from('orders_v2')
    .select(columnas, soloContar ? { count: 'exact', head: true } : undefined)
    .or(alcance)
    .is('encargo_de', null)
    // ADR 0029 F4: la lista pregunta por el COMPROMISO. `stages` trae ya valores de `status`
    // (ver `statusDePantalla`); las RPCs agregadas siguen aceptando ambos vocabularios porque
    // el panel de producción todavía manda los viejos.
    .in('status', stages);

  if (paymentStatus?.length) query = query.in('payment_status', paymentStatus);
  if (shippingStatus?.length) query = query.in('shipping_status', shippingStatus);
  if (channel?.length) query = filtrarPorCanal(query, channel, tenantId);
  if (billingStatus?.length) query = query.in('billing_status', billingStatus);
  if (onlyWithDue) query = query.gt('amount_due', 0);
  if (modalidad?.length === 1 && ['motorizado', 'agencia'].includes(modalidad[0])) query = query.eq('is_cod', modalidad[0] === 'motorizado');
  if (createdFrom) query = query.gte('fecha_efectiva', createdFrom);
  if (filtraPorRegistro) query = query.in('interno.registro_lo_hace', registraLaGuia as string[]);
  query = fecha.aplicar(query);

  // El buscador encuentra por todo lo del pedido (20261013200000): cada palabra, en su texto de búsqueda.
  for (const palabra of palabras) query = query.ilike('busqueda.texto', `%${palabra}%`);
  return query;
}

/**
 * El texto de búsqueda de cada pedido (20261013200000): número, cliente, teléfono, documento, dirección, distrito,
 * provincia, departamento, productos y SKU, guía, agencia, transportista, motorizado, tienda y notas, sin tildes. Lo
 * mantiene la base (`pedido_busqueda`, por disparadores) y lo lee quien puede ver el pedido (su RLS hereda la de
 * `orders_v2`). La lista, el total, los grupos y `fn_orders_group_v2` buscan con la misma regla: todas las palabras.
 *
 * 🐛 ANTES BUSCABA EN TRES COLUMNAS (cliente, teléfono, número), founder 13-oct: «no permite buscar por ciudad u otras cosas».
 */
const EMBED_DE_BUSQUEDA = 'busqueda:pedido_busqueda!inner(texto)';

/**
 * A qué pedidos mira la lista: los de todos los papeles, o sólo los propios cuando se trata de cobrar o de
 * filtrar por pago. El saldo y el pago de una venta son de quien la vendió: en la lista de Vitalex, «Con saldo»
 * y «Pagadas» filtraban las ventas de los drops por lo que pagó el cliente del drop (founder, 30-sep).
 */
export function alcanceDeLaLista(tenantId: string, soloLoPropio?: boolean): string {
  return soloLoPropio
    ? `tenant_id.eq.${tenantId}`
    : `tenant_id.eq.${tenantId},almacen_tenant_id.eq.${tenantId},courier_tenant_id.eq.${tenantId},proveedor_tenant_id.eq.${tenantId}`;
}

export async function fetchOrdersPage({
  sort = 'fecha_efectiva desc', offset = 0, limit = 25, ...filtros
}: OrdersPageParams): Promise<OrdersPage> {
  // La primera consulta de la lista sale a la red: tercer hito del arranque.
  marcarHito('pedido');
  let query = construirConsultaDePedidos(filtros);
  const { tenantId } = filtros;

  // Orden parametrizable (allowlist). Default created_at desc si la columna no es válida.
  const [rawCol, rawDir] = sort.split(' ');
  const sortCol = SORTABLE_COLUMNS.has(rawCol) ? rawCol : 'fecha_efectiva';
  const ascending = rawDir === 'asc';
  /*
   * ⭐ UNA FILA DE MÁS: así se sabe si hay página siguiente sin contar nada. Se pide `limit + 1`,
   *    se devuelve `limit`, y la sobrante sólo sirve como respuesta a «¿hay más?».
   *
   * 🪤 EL TECHO DE POSTGREST TAMBIÉN APLICA ACÁ. Si alguien pidiera `limit = 1000`, la fila de más
   *    nunca llegaría (el servidor corta en 1.000) y `hasNext` diría «no hay más» habiendo más. Se
   *    acota el pedido para que la fila centinela SIEMPRE entre.
   */
  const limitePedido = Math.min(limit, MAX_FILAS_POSTGREST - 1);
  query = query
    .order(sortCol, { ascending, nullsFirst: false })
    .order('order_number', { ascending }).order('id', { ascending }) // a igual fecha, el número de venta; el id, estable
    .range(offset, offset + limitePedido); // +1 implícito: `range` es inclusivo en ambos extremos

  const { data, error } = await query;
  // La respuesta volvio: de aca en adelante lo que tarde ya es el navegador, no la red.
  marcarHito('datos');
  if (error) throw error;
  const filas = (data ?? []) as unknown as OrderRecord[];
  const hasNext = filas.length > limitePedido;
  const items = hasNext ? filas.slice(0, limitePedido) : filas;
  // ⭐ El transportista se ve desde el primer cuadro: sale del envío embebido, sin otra consulta.
  ponerTransportistaDesdeElEnvio(items);
  // 🚨 La parte de quien no vendió viaja CON la fila, no en el enriquecimiento: es su cifra (ver `ponerLaParteDeQuienMira`).
  await ponerLaParteDeQuienMira(items, tenantId);
  /*
   * 🚨 ACA SE ESPERABAN LOS ADORNOS, Y ESO SUMABA UN VIAJE ENTERO AL CAMINO CRITICO. Las tres
   *    consultas —transportista, pedidos del cliente, miniaturas— van en paralelo entre ellas, pero
   *    DESPUES de la principal: ~165 ms medidos, con las filas ya en la mano y sin mostrarse.
   *
   * ⭐ AHORA NO SE ESPERAN. La lista devuelve sus filas y el enriquecimiento va aparte
   *    (`enriquecerPedidos`), que es como cargan Shopify y Linear: primero el dato que se vino a
   *    ver, despues el contexto.
   *
   * 🪤 Y NO ES "MOSTRAR DATOS A MEDIAS": las tres columnas que llegan tarde ya sabian mostrarse
   *    vacias —un pedido sin envio no tiene transportista, uno sin foto no tiene miniatura— asi que
   *    la pantalla no aprende un estado nuevo. Lo que cambia es cuando se llena, no si.
   */
  return { items, hasNext, total: null };
}

/**
 * El contexto de cada fila: transportista, cuantos pedidos tiene el cliente y las miniaturas.
 *
 * ⭐ SE LLAMA DESPUES DE PINTAR, no antes. Devuelve un arreglo NUEVO —no muta el que recibio— para
 *    que React vea que algo cambio: mutar los objetos en su lugar deja la pantalla igual y el
 *    enriquecimiento se pierde sin que nadie se entere. La parte de la liquidación no va acá: viaja con la fila.
 */
export async function enriquecerPedidos(
  items: OrderRecord[],
  tenantId: string,
): Promise<OrderRecord[]> {
  const copia = items.map((o) => ({ ...o }));
  await Promise.all([
    attachLineImages(copia, tenantId),
    attachCustomerStats(copia, tenantId),
    attachTransport(copia, tenantId),
  ]);
  return copia;
}

/**
 * El total exacto, cuando de verdad hace falta — y NUNCA en el camino crítico de la lista.
 *
 * ⭐ VIVE APARTE A PROPÓSITO. Es la consulta cara (206 ms en frío sobre 11.539 filas) y su
 *    resultado sólo alimenta la etiqueta «3 de 462 páginas». Separarla permite que la lista pinte
 *    con sus filas y que el número llegue después, sin que nadie espere por él.
 *
 * 🪤 Y SU CLAVE DE CACHÉ SON LOS FILTROS, NO LA PÁGINA. Cambiar de página no cambia el total: si
 *    la clave incluyera el `offset`, cada avance volvería a pagar los 206 ms — que es justo lo que
 *    pasaba cuando el conteo viajaba pegado a la página.
 */
export async function fetchOrdersTotal(
  p: Omit<OrdersPageParams, 'offset' | 'limit' | 'sort'>,
): Promise<number> {
  const { count, error } = await construirConsultaDePedidos(p, { soloContar: true });
  if (error) throw error;
  return count ?? 0;
}

// ── Ventas 2 · agregación MULTINIVEL server-side (fn_orders_group_v2 / _detail_v2) ──
// Reemplaza el grouping client-side (cap 500): el árbol + subtotales salen de la BD (ROLLUP)
// sobre TODO el dataset, y las filas de cada grupo hoja se cargan lazy por sus IDs.

/** Un nodo del árbol de agrupación. `keys` = valores por dimensión; los niveles agregados por
 *  ROLLUP salen `null` al final → el nivel del nodo = cantidad de keys no-nulas (prefijo). */
export interface OpsGroupNode {
  keys: (string | null)[];
  count: number;
  total: number;
  paid: number;
  due: number;
}

export interface OpsGroupTreeParams {
  tenantId: string;
  stages: string[];
  dims: string[]; // orden acumulativo, p.ej. ['date:month','salesperson']
  paymentStatus?: string[];
  shippingStatus?: string[];
  channel?: string[];
  billingStatus?: string[];
  search?: string;
  onlyWithDue?: boolean;
  dateFrom?: string | null;
  dateTo?: string | null;
}

/** Árbol de agrupación multinivel resuelto en la BD (ROLLUP) sobre TODO el dataset (no un lote). */
export async function fetchOpsGroupTree(p: OpsGroupTreeParams): Promise<OpsGroupNode[]> {
  const { data, error } = await supabase.rpc('fn_orders_group_v2', {
    p_tenant: p.tenantId,
    p_stages: p.stages,
    p_dims: p.dims,
    p_payment: p.paymentStatus?.length ? p.paymentStatus : null,
    p_shipping: p.shippingStatus?.length ? p.shippingStatus : null,
    p_channel: p.channel?.length ? p.channel : null,
    p_billing: p.billingStatus?.length ? p.billingStatus : null,
    p_search: p.search?.trim() || null,
    p_only_with_due: p.onlyWithDue ?? false,
    p_date_from: p.dateFrom ?? null,
    p_date_to: p.dateTo ?? null,
  });
  if (error) throw error;
  const nodes = ((data as { nodes?: unknown } | null)?.nodes ?? []) as Array<Record<string, unknown>>;
  return nodes.map((n) => ({
    keys: Array.isArray(n.keys) ? (n.keys as (string | null)[]) : [],
    count: Number(n.count ?? 0),
    total: Number(n.total ?? 0),
    paid: Number(n.paid ?? 0),
    due: Number(n.due ?? 0),
  }));
}

export interface OpsGroupLeafParams extends OpsGroupTreeParams {
  keys: string[]; // combo del grupo hoja (alineado con dims)
  sort?: string;
  limit?: number;
}

/** Rango [from, to) UTC del bucket de fecha. `key`='YYYY-MM-DD' = inicio del bucket en hora Lima
 *  (UTC-5, sin DST). Coincide EXACTO con `date_trunc(gran, sale_ts at tz Lima)` que agrupa el árbol. */
function saleBucketRange(key: string, gran: string): { from: string; to: string } {
  const [y, m, d] = key.split('-').map(Number);
  const start = new Date(Date.UTC(y, (m || 1) - 1, d || 1)); // fecha “desnuda” para aritmética de calendario
  const end = new Date(start);
  if (gran === 'year') end.setUTCFullYear(end.getUTCFullYear() + 1);
  else if (gran === 'quarter') end.setUTCMonth(end.getUTCMonth() + 3);
  else if (gran === 'month') end.setUTCMonth(end.getUTCMonth() + 1);
  else if (gran === 'week') end.setUTCDate(end.getUTCDate() + 7);
  else end.setUTCDate(end.getUTCDate() + 1);
  // 00:00 en Lima (UTC-5) = 05:00 UTC.
  const toUtc = (dt: Date) => `${dt.toISOString().slice(0, 10)}T05:00:00.000Z`;
  return { from: toUtc(start), to: toUtc(end) };
}

/** Pedidos de UN grupo hoja en 1 SOLO viaje: query directa sargable sobre `sale_ts` (misma que la
 *  lista plana, con embeds), en vez de RPC-ids→select→enrich. Mismo shape que fetchOrdersPage → toRow. */
export async function fetchOrdersForGroupLeaf(p: OpsGroupLeafParams): Promise<{ items: OrderRecord[]; total: number }> {
  const palabras = p.search ? palabrasDeBusqueda(p.search) : [];
  let q = supabase
    .from('orders_v2')
    // Con búsqueda, el texto de búsqueda del pedido entra en la consulta (20261013200000): ver `EMBED_DE_BUSQUEDA`.
    .select(palabras.length ? `${ORDER_COLUMNS_WITH_COD}, ${EMBED_DE_BUSQUEDA}` : ORDER_COLUMNS_WITH_COD, { count: 'exact' })
    .eq('tenant_id', p.tenantId)
    .in('status', p.stages);
  // Un filtro sargable por dimensión del combo: eq para categóricas, rango sale_ts para fechas.
  p.dims.forEach((dim, i) => {
    const key = p.keys[i];
    if (dim === 'salesperson') {
      // Vendedor efectivo: 'u:<id>' = usuario (salesperson_id), 'r:<id>' = catálogo (sales_rep_id).
      if (key === '∅') q = q.is('salesperson_id', null).is('sales_rep_id', null);
      else if (key.startsWith('u:')) q = q.eq('salesperson_id', key.slice(2));
      else if (key.startsWith('r:')) q = q.eq('sales_rep_id', key.slice(2));
      else q = q.eq('salesperson_id', key); // compat: claves viejas sin prefijo
    }
    else if (dim === 'payment') q = key === '∅' ? q.is('payment_status', null) : q.eq('payment_status', key);
    else if (dim === 'stage') q = key === '∅' ? q.is('lifecycle_stage', null) : q.eq('lifecycle_stage', key);
    else if (dim === 'channel') q = key === '∅' ? q.is('channel', null) : q.eq('channel', key);
    else if (dim === 'billing') q = key === '∅' ? q.is('billing_status', null) : q.eq('billing_status', key);
    else if (dim.startsWith('date:')) {
      const { from, to } = saleBucketRange(key, dim.split(':')[1]);
      q = q.gte('sale_ts', from).lt('sale_ts', to);
    }
  });
  if (p.paymentStatus?.length) q = q.in('payment_status', p.paymentStatus);
  if (p.shippingStatus?.length) q = q.in('shipping_status', p.shippingStatus);
  if (p.channel?.length) q = q.in('channel', p.channel);
  if (p.billingStatus?.length) q = q.in('billing_status', p.billingStatus);
  if (p.onlyWithDue) q = q.gt('amount_due', 0);
  for (const palabra of palabras) q = q.ilike('busqueda.texto', `%${palabra}%`);
  const [rawCol, rawDir] = (p.sort ?? 'fecha_efectiva desc').split(' ');
  const sortCol = SORTABLE_COLUMNS.has(rawCol) ? rawCol : 'fecha_efectiva';
  const ascending = rawDir === 'asc';
  q = q.order(sortCol, { ascending, nullsFirst: false }).order('order_number', { ascending }).order('id', { ascending }).range(0, (p.limit ?? 100) - 1);
  const { data, error, count } = await q;
  if (error) throw error;
  const items = (data ?? []) as unknown as OrderRecord[];
  // Light: el transportista (columna visible) y, en las ventas ajenas, la parte de quien mira (la cifra de Dinero).
  await Promise.all([attachTransport(items, p.tenantId), ponerLaParteDeQuienMira(items, p.tenantId)]);
  return { items, total: count ?? 0 };
}

/**
 * Resuelve, por pedido, el TRANSPORTISTA (carrier del último envío) y su TIPO
 * (Agencia/Motorizado) cruzando el nombre contra `logistics_couriers` (couriers del
 * sistema con tenant_id NULL + los del tenant). UNA query batch por página; degrada
 * a sin-tipo si falla o si el carrier no está en el catálogo.
 */
async function attachTransport(orders: OrderRecord[], tenantId: string): Promise<void> {
  // El nombre y la agencia ya vienen puestos desde el envío embebido (ponerTransportistaDesdeElEnvio,
  // al recibir la lista). Acá sólo se cruza contra el catálogo: TIPO y LOGO.
  ponerTransportistaDesdeElEnvio(orders);
  const anyCarrier = orders.some((o) => o.transport_carrier);
  if (!anyCarrier) return;
  // Más los de Smarty Drop: son de otra cuenta y el catálogo del negocio no los trae (ver `couriersDeSmartyDropPorNombre`).
  const [{ data, error }, deSmartyDrop] = await Promise.all([supabase.from('logistics_couriers').select('name, type, logo_url')
    .or(`tenant_id.eq.${tenantId},tenant_id.is.null`), couriersDeSmartyDropPorNombre()]);
  // El transportista ya quedo puesto arriba desde el envio; lo que falla aca es el LOGO y el
  // tipo. Se registra para que un catalogo de couriers roto no se vea como pedidos sin courier.
  if (error || !data) { logError('No se pudo leer el catalogo de couriers', error); return; }
  // name → { type, logo } del catálogo de couriers (para tipo + logo de marca); el del negocio manda.
  const byName = new Map(deSmartyDrop);
  for (const c of data) {
    const row = c as { name: string | null; type: string | null; logo_url: string | null };
    if (row.name) byName.set(row.name.toLowerCase(), { type: (row.type ?? '').toLowerCase(), logo: row.logo_url || null });
  }
  for (const o of orders) {
    const carrier = o.transport_carrier;
    if (!carrier) continue;
    const key = carrier.toLowerCase();
    let hit = byName.get(key);
    if (!hit) {
      // Fuzzy: nombre de courier contenido en el carrier (p. ej. "Olva Courier" → "Olva").
      for (const [n, v] of byName) { if (n.length >= 3 && key.includes(n)) { hit = v; break; } }
    }
    o.transport_type = hit?.type ?? null;
    o.transport_logo_url = hit?.logo ?? null;
  }
}

/** Estructura mínima de una fila con contacto que puede recibir el conteo de pedidos. */
type WithContact = { contact_id: string | null; customer_orders_count?: number | null };

/**
 * Adjunta `customer_orders_count` (cuántos pedidos tiene el cliente) para el
 * popover de Cliente — estilo Shopify "N pedidos". Fuente: vista Cliente 360
 * `crm_customer_list` (agrega por contacto). UNA query batch por página.
 * Degrada a sin-conteo si falla o si el pedido no tiene contacto vinculado.
 * Exportada para reusarse en otras listas (p. ej. Pedidos online).
 */
export async function attachCustomerStats(orders: WithContact[], tenantId: string): Promise<void> {
  const ids = new Set<string>();
  for (const o of orders) if (o.contact_id) ids.add(o.contact_id);
  if (ids.size === 0) return;
  const { data, error } = await supabase
    .from('crm_customer_list')
    .select('id, orders_count')
    .eq('tenant_id', tenantId)
    .in('id', [...ids]);
  /*
   * 🚨 ACÁ SE VOLVÍA SIN DECIR NADA, Y ESO CONVERTÍA UN FALLO EN UN CERO. La columna «Pedidos del
   *    cliente» quedaba en 0 con la misma cara que un cliente nuevo de verdad: sin error en
   *    pantalla, sin nada en los logs, sin forma de distinguirlos.
   *
   * ⭐ Ahora el fallo se REGISTRA y el dato queda en nulo — que la lista pinta como «—», no como 0.
   *    Un dato que no se pudo traer y un dato que vale cero dejan de parecerse.
   */
  if (error || !data) {
    logError('No se pudieron contar los pedidos por cliente', error);
    for (const o of orders) if (o.contact_id) o.customer_orders_count = null;
    return;
  }
  const countById = new Map<string, number>(
    data.map((c) => [c.id as string, Number((c as { orders_count: number | null }).orders_count) || 0]),
  );
  for (const o of orders) if (o.contact_id) o.customer_orders_count = countById.get(o.contact_id) ?? 0;
}

/** Estructura mínima de una fila con líneas que pueden recibir miniatura. */
type WithLines = { lines?: ({ product_id?: string | null; image_url?: string | null } | null)[] | null };

/**
 * Adjunta `image_url` a cada línea de producto (miniatura estilo Shopify en el
 * popover de "Artículos"). order_lines NO tiene FK a products → no se puede
 * embeber con PostgREST; se resuelve con UNA query batch por página (barata:
 * ~la página de pedidos, no el dataset). Degrada a sin-imagen si falla.
 * Exportada para reusarse en otras listas que embeben líneas (p. ej. Pedidos online).
 */
export async function attachLineImages(orders: WithLines[], tenantId: string): Promise<void> {
  const ids = new Set<string>();
  for (const o of orders) for (const l of o.lines ?? []) if (l?.product_id) ids.add(l.product_id);
  if (ids.size === 0) return;
  const { data, error } = await supabase
    .from('products')
    .select('id, image_url')
    .eq('tenant_id', tenantId)
    .in('id', [...ids]);
  // Degradar a sin-imagen esta bien (una miniatura ausente no miente). Callarse, no: sin este
  // registro, un fallo sistemático al leer productos se vería como un catálogo sin fotos.
  if (error || !data) { logError('No se pudieron traer las miniaturas de las lineas', error); return; }
  const imgById = new Map<string, string | null>(
    data.map((p) => [p.id as string, (p.image_url as string | null) ?? null]),
  );
  for (const o of orders) for (const l of o.lines ?? []) {
    if (l?.product_id) l.image_url = imgById.get(l.product_id) ?? null;
  }
}

/** Agregados (KPIs) calculados server-side vía RPC. Keys planas. */
export interface OpsKpis {
  count: number;
  total: number;
  paid: number;
  due: number;
  pay_unpaid: number;
  pay_partial: number;
  pay_paid: number;
  ship_pending: number;
  ship_transit: number;
  ship_delivered: number;
  ship_returned: number;
  stage_quotation: number;
  stage_sale: number;
  stage_fulfilled: number;
  overdue: number;
}

const EMPTY_KPIS: OpsKpis = {
  count: 0, total: 0, paid: 0, due: 0,
  pay_unpaid: 0, pay_partial: 0, pay_paid: 0,
  ship_pending: 0, ship_transit: 0, ship_delivered: 0, ship_returned: 0,
  stage_quotation: 0, stage_sale: 0, stage_fulfilled: 0, overdue: 0,
};

/** KPIs agregados del tenant para las etapas pedidas (una sola query agregada). */
export async function fetchOpsKpis(tenantId: string, stages: string[]): Promise<OpsKpis> {
  const { data, error } = await supabase.rpc('ops_orders_kpis', { p_tenant: tenantId, p_stages: stages });
  if (error) throw error;
  return { ...EMPTY_KPIS, ...((data as Partial<OpsKpis>) ?? {}) };
}

// ── KPIs v2 (estilo Shopify): valor actual + periodo anterior + serie por bucket ──
/**
 * El período que gobierna la pantalla — las tarjetas Y la lista.
 *
 * ⭐ `todo` NO ES «UNA OPCIÓN MÁS»: es la que faltaba, y es la que corresponde a las colas de
 *    trabajo. Un STOCK —la plata que te deben, lo que falta despachar— simplemente ES; no se mide
 *    por período. «Deuda de los últimos 7 días» no quiere decir nada, y de hecho la deuda más
 *    vieja es la que más importa. Un FLUJO —ventas hechas— sí vive dentro de un lapso.
 */
export type OpsKpiPeriod = 'today' | '7d' | '30d' | 'todo';
export const OPS_KPI_PERIODS: { value: OpsKpiPeriod; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: '7d', label: '7 días' },
  { value: '30d', label: '30 días' },
  { value: 'todo', label: 'Todo' },
];
export type OpsKpiMetricKey =
  | 'orders' | 'revenue' | 'paid' | 'due'
  | 'pay_unpaid' | 'pay_partial'
  | 'ship_pending' | 'ship_transit' | 'ship_delivered' | 'ship_returned';
export interface OpsKpiMetric {
  value: number;
  previous: number;
  series: number[];
}
export interface OpsKpisV2 {
  period: OpsKpiPeriod;
  buckets: number;
  metrics: Record<OpsKpiMetricKey, OpsKpiMetric>;
}

/**
 * KPIs v2 server-side: por métrica { value (periodo actual), previous (periodo
 * anterior equivalente), series (buckets del actual: 24 horas para Hoy, N días).
 * Alimenta el KpiBar v2 (delta period-over-period + sparkline).
 */
/**
 * Desde qué fecha mira un período. `null` = sin piso (todo).
 *
 * 🪤 VIVE ACÁ Y NO EN LA PANTALLA para que la lista y las tarjetas no puedan calcular el mismo
 *    período de dos maneras. El día que se agregue «este mes», se agrega una vez.
 *
 * 🪤 Y usa la zona de Lima, igual que la función de la base: con UTC, «hoy» arrancaría a las 19:00
 *    del día anterior y las primeras ventas de la mañana caerían fuera.
 */
export function desdeCuandoMira(period: OpsKpiPeriod): string | null {
  if (period === 'todo') return null;
  const ahoraLima = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Lima' }));
  const dias = period === '7d' ? 6 : period === '30d' ? 29 : 0;
  ahoraLima.setDate(ahoraLima.getDate() - dias);
  ahoraLima.setHours(0, 0, 0, 0);
  return ahoraLima.toISOString();
}

export async function fetchOpsKpisV2(
  tenantId: string,
  stages: string[],
  period: OpsKpiPeriod,
): Promise<OpsKpisV2 | null> {
  const { data, error } = await supabase.rpc('ops_orders_kpis_v2', {
    p_tenant: tenantId, p_stages: stages, p_period: period,
  });
  if (error) throw error;
  return (data as unknown as OpsKpisV2 | null) ?? null;
}

// ── O6: dashboard / analítica (read model agregado por RPC) ─────────────────
export interface OpsDashboard {
  sales_count: number;
  revenue: number;
  collected: number;
  pending: number;
  delivered_count: number;
  by_channel: { channel: string; count: number; revenue: number }[];
  daily: { day: string; count: number; revenue: number }[];
}

const EMPTY_DASHBOARD: OpsDashboard = {
  sales_count: 0, revenue: 0, collected: 0, pending: 0, delivered_count: 0, by_channel: [], daily: [],
};

/** Métricas de ventas del período (agregadas server-side). */
export async function fetchOpsDashboard(tenantId: string, days: number): Promise<OpsDashboard> {
  const { data, error } = await supabase.rpc('ops_dashboard', { p_tenant: tenantId, p_days: days });
  if (error) throw error;
  return { ...EMPTY_DASHBOARD, ...((data as Partial<OpsDashboard>) ?? {}) };
}

// ── O5: settings de Operaciones (modo de confirmación por canal) ─────────────
// ConfirmMode y defaultConfirmMode viven en orderMath (re-exportados arriba).

/** Modo de confirmación configurado por canal (los no seteados usan el default). */
export async function fetchOrderSettings(tenantId: string): Promise<Record<string, ConfirmMode>> {
  const { data, error } = await supabase
    .from('tenant_order_settings')
    .select('channel, confirm_mode')
    .eq('tenant_id', tenantId);
  if (error) throw error;
  const map: Record<string, ConfirmMode> = {};
  for (const r of (data ?? []) as { channel: string; confirm_mode: ConfirmMode }[]) map[r.channel] = r.confirm_mode;
  return map;
}

/** Upsert del modo de confirmación de un canal. */
export async function saveOrderSetting(tenantId: string, channel: string, mode: ConfirmMode): Promise<void> {
  const { error } = await supabase
    .from('tenant_order_settings')
    .upsert({ tenant_id: tenantId, channel, confirm_mode: mode, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id,channel' });
  if (error) throw error;
}

/**
 * Elimina un pedido (y su cascada: líneas, eventos, pagos, envíos, documentos). Los refs externos
 * (leads CRM, links de marketplace, movimientos de inventario) quedan con la referencia en NULL, no
 * se borran. Via RPC fn_delete_order (guardada por tenant). Devuelve true si borró algo.
 */
export async function deleteOrder(orderId: string, tenantId: string): Promise<void> {
  const { data, error } = await supabase.rpc('fn_delete_order', { p_order_id: orderId, p_tenant: tenantId });
  if (error) throw new Error(error.message);
  if (Number(data ?? 0) === 0) throw new Error('El pedido no existe o no pertenece a tu tienda.');
  olvidarListasDePedidos();
}

/** Borrado MASIVO de pedidos (selección múltiple). Devuelve cuántos borró. Via RPC fn_delete_orders. */
export async function deleteOrders(orderIds: string[], tenantId: string): Promise<number> {
  const { data, error } = await supabase.rpc('fn_delete_orders', { p_order_ids: orderIds, p_tenant: tenantId });
  if (error) throw new Error(error.message);
  olvidarListasDePedidos();
  return Number(data ?? 0);
}

// ── Fase R: miembros y roles de Operaciones ─────────────────────────────────
export interface OpsMember {
  user_id: string;
  name: string;
  email: string | null;
  /** Su WhatsApp: el del perfil o, si no tiene, la línea conectada del negocio (`fn_contacto_de_persona`). */
  telefono: string | null;
  staff_role: string;
  ops_role: OpsRole | null; // null = sin asignar (cae a 'admin')
  sales_team_id: string | null; // equipo de venta al que pertenece (Odoo)
}

/** El equipo con su rol de Operaciones. ⭐ El dueño es del equipo (en un drop no hay nadie más): `fn_equipo_del_negocio`. */
export async function fetchOpsMembers(tenantId: string): Promise<OpsMember[]> {
  const [{ data: equipo, error }, { data: members }] = await Promise.all([
    supabase.rpc('fn_equipo_del_negocio', { p_tenant: tenantId }),
    supabase.from('tenant_ops_members').select('user_id, ops_role, sales_team_id').eq('tenant_id', tenantId),
  ]);
  if (error) throw error;
  const memberMap = new Map((members ?? []).map((m) => [(m as { user_id: string }).user_id, m as { ops_role: OpsRole | null; sales_team_id: string | null }]));
  return ((equipo ?? []) as { user_id: string; nombre: string | null; email: string | null; telefono: string | null; rol: string }[])
    .map((e) => ({
      user_id: e.user_id,
      name: e.nombre || e.email || 'Usuario',
      email: e.email,
      telefono: e.telefono,
      staff_role: e.rol,
      ops_role: memberMap.get(e.user_id)?.ops_role ?? null,
      sales_team_id: memberMap.get(e.user_id)?.sales_team_id ?? null,
    }));
}

/** Equipo de venta (entidad que el tenant crea; los comerciales pertenecen a uno). */
export interface SalesTeam {
  id: string;
  tenant_id: string;
  name: string;
  created_at: string;
}

/** Equipos de venta del tenant. Resiliente si la tabla aún no existe. */
export async function fetchSalesTeams(tenantId: string): Promise<SalesTeam[]> {
  const { data, error } = await supabase
    .from('sales_teams')
    .select('id, tenant_id, name, created_at')
    .eq('tenant_id', tenantId)
    .order('name');
  if (error) {
    if (/sales_teams|does not exist|relation/i.test(error.message)) return [];
    throw error;
  }
  return (data ?? []) as unknown as SalesTeam[];
}

/** Crea un equipo de venta y devuelve el registro. */
export async function createSalesTeam(tenantId: string, name: string): Promise<SalesTeam> {
  const { data, error } = await supabase
    .from('sales_teams')
    .insert({ tenant_id: tenantId, name: name.trim() })
    .select('id, tenant_id, name, created_at')
    .single();
  if (error) throw error;
  return data as unknown as SalesTeam;
}

/** Renombra un equipo de venta. */
export async function renameSalesTeam(tenantId: string, teamId: string, name: string): Promise<void> {
  const { error } = await supabase.from('sales_teams').update({ name: name.trim() }).eq('id', teamId).eq('tenant_id', tenantId);
  if (error) throw error;
}

/** Elimina un equipo de venta (los pedidos/miembros quedan sin equipo por el ON DELETE SET NULL). */
export async function deleteSalesTeam(tenantId: string, teamId: string): Promise<void> {
  const { error } = await supabase.from('sales_teams').delete().eq('id', teamId).eq('tenant_id', tenantId);
  if (error) throw error;
}

/** Asigna el equipo de venta de un miembro (su equipo por defecto).
 *  ops_role es NOT NULL: si el miembro aún no tiene fila, se crea con 'admin' (el fallback por defecto). */
export async function setOpsMemberTeam(tenantId: string, userId: string, salesTeamId: string | null): Promise<void> {
  const { data: existing } = await supabase
    .from('tenant_ops_members').select('ops_role').eq('tenant_id', tenantId).eq('user_id', userId).maybeSingle();
  const opsRole = (existing as { ops_role: OpsRole } | null)?.ops_role ?? 'admin';
  const { error } = await supabase
    .from('tenant_ops_members')
    .upsert({ tenant_id: tenantId, user_id: userId, ops_role: opsRole, sales_team_id: salesTeamId, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id,user_id' });
  if (error) throw error;
}

/** Asigna el comercial (usuario), el vendedor del catálogo (sales_rep) y/o el equipo de un pedido. */
export async function setOrderSalesAssignment(
  order: OrderRecord,
  patch: { salesperson_id?: string | null; sales_rep_id?: string | null; sales_team_id?: string | null },
): Promise<void> {
  const { error } = await supabase.from('orders_v2').update(patch).eq('id', order.id).eq('tenant_id', order.tenant_id);
  if (error) throw error;
}

export interface SalesRep { id: string; name: string; is_active: boolean }

/** Catálogo de vendedores del tenant (los de Odoo, SIN usuario). Incluye archivados (is_active=false). */
export async function fetchSalesReps(tenantId: string): Promise<SalesRep[]> {
  const { data, error } = await supabase.rpc('fn_sales_reps_list', { p_tenant: tenantId });
  if (error) return [];
  return ((data ?? []) as { id: string; name: string; is_active: boolean }[])
    .map((r) => ({ id: r.id, name: r.name, is_active: r.is_active }));
}

/** Asigna el rol de Operaciones a un usuario. */
export async function setOpsMemberRole(tenantId: string, userId: string, role: OpsRole): Promise<void> {
  const { error } = await supabase
    .from('tenant_ops_members')
    .upsert({ tenant_id: tenantId, user_id: userId, ops_role: role, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id,user_id' });
  if (error) throw error;
}

/**
 * Un pedido para el detalle. Degrada si O1 (notes/tags) no está aplicada.
 *
 * ⭐ POR ID, EL PEDIDO SE BUSCA EN EL MISMO ALCANCE QUE LA LISTA: lo mío, lo que despacho y lo que
 *    entrego (20260925162000). Sin eso, el almacén veía el encargo en Ventas y al abrirlo le decía
 *    «Pedido no encontrado» (founder, 25-sep): el detalle lo buscaba sólo en su cuenta.
 * 🪤 POR NÚMERO, SÓLO LO PROPIO: el número es único por negocio, no entre negocios. Un pedido ajeno
 *    se abre por su id (ver `clave_del_pedido` en la fila).
 */
async function fetchOrderBy(tenantId: string, column: 'id' | 'order_number', value: string): Promise<OrderRecord | null> {
  const sel = (cols: string) => {
    const q = supabase.from('orders_v2').select(`${cols}, almacen_tenant_id, courier_tenant_id, proveedor_tenant_id`).eq(column, value);
    return (column === 'id'
      ? q.or(`tenant_id.eq.${tenantId},almacen_tenant_id.eq.${tenantId},courier_tenant_id.eq.${tenantId},proveedor_tenant_id.eq.${tenantId}`)
      : q.eq('tenant_id', tenantId)).maybeSingle();
  };

  let res = await sel(ORDER_DETAIL_COLUMNS);
  if (res.error && /does not exist/i.test(res.error.message)) {
    res = await sel(ORDER_COLUMNS); // O1 aún no aplicada: sin notes/tags
  }
  if (res.error) throw res.error;
  if (!res.data) return null;
  const rec = res.data as unknown as OrderRecord;
  return { ...rec, notes: rec.notes ?? null, tags: rec.tags ?? [], shipping_cost: Number(rec.shipping_cost ?? 0), freight_payer: rec.freight_payer ?? 'origin' };
}

/** Lo mínimo para listar un pedido en un popover: número, fecha, total y si está pagado. */
export interface PedidoResumido {
  id: string;
  order_number: string | null;
  grand_total: number | null;
  created_at: string | null;
  payment_status: string | null;
}

/**
 * Los últimos pedidos de una lista de ids, resumidos.
 *
 * POR QUÉ EXISTE ACÁ Y NO EN LA PANTALLA. El desplegable de «Pedidos» en la lista de Clientes
 * necesita esto, y lo primero que escribí fue un `supabase.from('orders_v2')` dentro del propio
 * popover. El gate `check:puerta-pedidos` lo rechazó, y tenía razón:
 *
 * 🪤 EL PEDIDO ESTÁ CAMBIANDO DE MODELO (ADR 0029). Una consulta suelta contra las columnas viejas
 *    es la que va a quedar mintiendo cuando la F6 jubile el enum de etapa — y nadie va a ir a
 *    buscarla dentro de un archivo de configuración de listas. Por eso el dominio tiene una puerta.
 *
 * 🪤 Y SE BUSCA POR IDS, no por teléfono ni por nombre: quien llama ya los tiene (el listado de
 *    clientes los calcula al agrupar). Por teléfono traería los de otro cliente que comparte
 *    número —pasa con familias—; por nombre, los de cualquier homónimo.
 */
export async function pedidosResumidosPorIds(
  tenantId: string,
  ids: string[],
  limite = 6,
): Promise<PedidoResumido[]> {
  if (!tenantId || ids.length === 0) return [];
  const { data, error } = await supabase
    .from('orders_v2')
    .select('id, order_number, grand_total, created_at, payment_status')
    .eq('tenant_id', tenantId)
    // Se acota la lista de ids antes de pedirla: un cliente con 200 pedidos no necesita mandar
    // 200 uuid por la URL para mostrar seis.
    .in('id', ids.slice(0, limite * 3))
    .order('created_at', { ascending: false })
    .limit(limite);
  if (error) throw error;
  return (data ?? []) as PedidoResumido[];
}

export function fetchOrder(tenantId: string, orderId: string): Promise<OrderRecord | null> {
  return fetchOrderBy(tenantId, 'id', orderId);
}

/** Por número de pedido (único por tenant, ver unique(tenant_id, order_number)) — URLs legibles. */
export function fetchOrderByNumber(tenantId: string, orderNumber: string): Promise<OrderRecord | null> {
  return fetchOrderBy(tenantId, 'order_number', orderNumber);
}

const ORDER_KEY_IS_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
/** Resuelve por UUID (bookmarks viejos) o por número — y `S-16339` de ayer llega al mismo pedido que `S16339`. */
export function fetchOrderByKey(tenantId: string, key: string): Promise<OrderRecord | null> {
  return ORDER_KEY_IS_UUID.test(key) ? fetchOrder(tenantId, key) : fetchOrderByNumber(tenantId, elNumeroComoSeGuarda(key));
}

/**
 * Identidad de la tienda (nombre del emisor) para el rótulo de envío / branding del comprobante.
 * Fuente: `tenants.name` (lo que muestra el Topbar). Best-effort: degrada a vacío si falla.
 * Reconstruida tras perderse en un checkout errado (la usa el rótulo de Entrega y de la lista).
 */
export async function fetchStoreIdentity(tenantId: string): Promise<{ name: string }> {
  try {
    const { data } = await supabase.from('tenants').select('name').eq('id', tenantId).maybeSingle();
    return { name: ((data as { name?: string | null } | null)?.name ?? '').trim() };
  } catch {
    return { name: '' };
  }
}

/** Perfil COD completo (dirección de entrega, documento, compromiso de cobro). 1:1 opcional. */
export interface OrderCodProfile {
  department: string | null;
  province: string | null;
  district: string | null;
  address: string | null;
  address_ref: string | null;
  doc_type: string | null;
  doc_number: string | null;
  business_name: string | null;
  expected_advance: number;
  expected_balance: number;
  commitment_date: string | null;
  commitment_notes: string | null;
  collection_status: string;
  /** Coordenadas del cliente (mapa del checkout). Las usa Eva para caer el pin en su zona.
   *  Opcionales: no todos los flujos que arman un OrderCodProfile las tienen a mano. */
  geo_lat?: number | null;
  geo_lng?: number | null;
}

export async function fetchOrderCodProfile(tenantId: string, orderId: string): Promise<OrderCodProfile | null> {
  const { data, error } = await supabase
    .from('order_delivery_profile')
    .select('department, province, district, address, address_ref, doc_type, doc_number, business_name, expected_advance, expected_balance, commitment_date, commitment_notes, collection_status, geo_lat, geo_lng')
    .eq('tenant_id', tenantId)
    .eq('order_id', orderId)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as OrderCodProfile) ?? null;
}

// ── O4: documentos (boleta/factura/guía) con numeración por tenant ───────────
export type DocKind = 'receipt' | 'boleta' | 'factura' | 'guia' | 'nota_venta';

export const DOC_KIND_LABEL: Record<DocKind, string> = {
  receipt: 'Comprobante', boleta: 'Boleta', factura: 'Factura', guia: 'Guía de remisión', nota_venta: 'Nota de venta',
};
// Series SUNAT de Smarty: F002/B002 (Odoo usa F001/B001; series distintas = sin choque de correlativo).
const DOC_SERIES: Record<DocKind, string> = { receipt: 'R001', boleta: 'B002', factura: 'F002', guia: 'T001', nota_venta: 'NV01' };

export interface OrderDocument {
  id: string;
  order_id: string;
  tenant_id: string;
  doc_kind: DocKind;
  series: string | null;
  number: string | null;
  external_ref: string | null;
  issued_at: string | null;
  /** Vencimiento (editable; por defecto = fecha de emisión). */
  due_date: string | null;
  /** Ciclo de vida LOCAL: draft | published (NUMERADO, no emitido) | sent | voided. 🪤 «Emitido» lo dice `cdr_status`, no esto: ver comprobanteEstado.ts. */
  status: 'draft' | 'published' | 'sent' | 'voided';
  created_at: string;
  /** Datos fiscales del CPE (se llenan al emitir a SUNAT vía cpe-emit). */
  qr_string?: string | null;
  doc_hash?: string | null;
  cdr_status?: string | null;
  /** Quién pidió emitirlo (cpe-emit lo firma); null = Smarty. 20261026180000. */
  actor_id?: string | null;
}

export async function fetchOrderDocuments(tenantId: string, orderId: string): Promise<OrderDocument[]> {
  const { data, error } = await supabase
    .from('order_documents')
    .select('id, order_id, tenant_id, doc_kind, series, number, external_ref, issued_at, due_date, status, created_at, qr_string, doc_hash, cdr_status, actor_id')
    .eq('tenant_id', tenantId)
    .eq('order_id', orderId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as OrderDocument[];
}

/** Enviar a SUNAT (BETA): por ahora solo marca el comprobante como enviado. Fase 2 = transmisión real. */
export async function sendDocumentToSunat(tenantId: string, docId: string): Promise<void> {
  const { error } = await supabase.from('order_documents').update({ status: 'sent' }).eq('id', docId).eq('tenant_id', tenantId);
  if (error) throw error;
}

/** Anular el comprobante (comunicación de baja). En beta solo marca el estado. */
export async function voidDocument(tenantId: string, docId: string): Promise<void> {
  const { error } = await supabase.from('order_documents').update({ status: 'voided' }).eq('id', docId).eq('tenant_id', tenantId);
  if (error) throw error;
}

/**
 * Restablecer a borrador: NO borra el comprobante — lo deja en 'draft' y libera el correlativo
 * (serie/número). Así el borrador PERSISTE (no desaparece al cambiar de página) y se puede editar
 * tipo/fechas y re-emitir. Solo permitido si NO se envió a SUNAT.
 */
export async function resetDocumentToDraft(tenantId: string, docId: string): Promise<void> {
  const { error } = await supabase.from('order_documents')
    .update({ status: 'draft', series: null, number: null })
    .eq('id', docId).eq('tenant_id', tenantId);
  if (error) throw error;
}

/**
 * Emite (publica) el comprobante con número correlativo por tenant (next_document_number,
 * concurrency-safe). Si `existingDocId` viene (re-emisión de un borrador restablecido), ACTUALIZA
 * esa fila reusándola; si no, INSERTA una nueva. Queda en estado 'published'. Base para
 * e-invoicing SUNAT externo (external_ref se llena luego desde el proveedor).
 */
export async function emitInvoice(
  order: OrderRecord,
  input: { existingDocId?: string | null; docKind: DocKind; issuedAt?: string | null; dueDate?: string | null },
): Promise<void> {
  // Boleta/factura numeran con el contador propio de la serie SUNAT (cpe_series) — el MISMO que reusa
  // cpe-emit al enviar. La serie es CONFIGURABLE por tenant (tenant_cpe_settings): así este tenant puede
  // CONTINUAR la serie que traía de Odoo (B001/F001) en vez de abrir una nueva. Fallback a la constante.
  const isSunat = input.docKind === 'boleta' || input.docKind === 'factura';
  let series = DOC_SERIES[input.docKind];
  if (isSunat) {
    const { data: cfg } = await supabase.from('tenant_cpe_settings')
      .select('serie_boleta, serie_factura').eq('tenant_id', order.tenant_id).maybeSingle();
    const s = cfg as { serie_boleta?: string | null; serie_factura?: string | null } | null;
    series = (input.docKind === 'factura' ? s?.serie_factura : s?.serie_boleta) || DOC_SERIES[input.docKind];
  }
  const { data: num, error: numErr } = isSunat
    ? await supabase.rpc('next_cpe_number', {
      p_tenant: order.tenant_id, p_type: input.docKind === 'factura' ? '01' : '03', p_serie: series,
    })
    : await supabase.rpc('next_document_number', {
      p_tenant: order.tenant_id, p_type: input.docKind, p_prefix: '',
    });
  if (numErr) throw numErr;
  const fields = {
    doc_kind: input.docKind,
    series,
    number: String(num),
    issued_at: input.issuedAt ?? new Date().toISOString(),
    due_date: input.dueDate ?? null,
    status: 'published' as const,
  };
  if (input.existingDocId) {
    const { error } = await supabase.from('order_documents').update(fields).eq('id', input.existingDocId).eq('tenant_id', order.tenant_id);
    if (error) throw error;
  } else {
    const { error } = await supabase.from('order_documents').insert({ order_id: order.id, tenant_id: order.tenant_id, ...fields });
    if (error) throw error;
  }
}

/** Edita los datos del cliente del pedido (nombre/teléfono/email). */
export async function updateOrderCustomer(
  order: OrderRecord,
  patch: Partial<{ customer_name: string | null; customer_phone: string | null; customer_email: string | null }>,
): Promise<void> {
  const { error } = await supabase.from('orders_v2').update(patch).eq('id', order.id).eq('tenant_id', order.tenant_id);
  if (error) throw error;
}

/** Upsert de campos del perfil COD (dirección/documento). Crea el perfil si no existe. */
export async function updateCodProfile(
  order: OrderRecord,
  patch: Partial<{ address: string | null; address_ref: string | null; district: string | null; province: string | null; department: string | null; doc_type: string | null; doc_number: string | null; geo_lat: number | null; geo_lng: number | null; envio_diferente: boolean }>,
): Promise<void> {
  const { error } = await supabase
    .from('order_delivery_profile')
    .upsert({ order_id: order.id, tenant_id: order.tenant_id, ...patch }, { onConflict: 'order_id' });
  if (error) throw error;
}

export interface ResolvedLocation {
  lat: number;
  lng: number;
  district: string | null;
  address: string | null;
  department: string | null;
  province: string | null;
  display_name: string | null;
  source: string;
}

export interface GeoContext { district?: string | null; province?: string | null; department?: string | null; lat?: number | null; lng?: number | null }
export interface LocationSuggestion { lat: number; lng: number; display_name: string }

/**
 * Resuelve una ubicación a un punto exacto + distrito/dirección (edge fn resolve-location).
 * Acepta un pin (lat/lng) o un texto pegado: link de Maps, coordenadas o dirección.
 * El `context` (distrito/departamento del cliente) sesga el geocode: sin él, "av lima" de un
 * cliente de Ate cae en Arequipa.
 */
export async function resolveLocation(input: { query?: string; lat?: number; lng?: number; context?: GeoContext }): Promise<ResolvedLocation | null> {
  const { data, error } = await supabase.functions.invoke('resolve-location', { body: input });
  const res = (data ?? {}) as { ok?: boolean; error?: string } & Partial<ResolvedLocation>;
  if (error || !res.ok || typeof res.lat !== 'number') {
    throw new Error(res?.error || error?.message || 'No se pudo resolver la ubicación.');
  }
  return res as ResolvedLocation;
}

/** Sugerencias de dirección para el autocompletado (sesgadas por el distrito del cliente). */
export async function suggestLocations(query: string, context?: GeoContext): Promise<LocationSuggestion[]> {
  const { data } = await supabase.functions.invoke('resolve-location', { body: { mode: 'suggest', query, context } });
  const res = (data ?? {}) as { ok?: boolean; suggestions?: LocationSuggestion[] };
  return res.suggestions ?? [];
}

export interface ProductSearchResult { id: string; name: string; price: number; image_url: string | null }

/** Busca productos del catálogo por nombre (autocompletado de líneas estilo Odoo). */
export async function searchProducts(tenantId: string, query: string): Promise<ProductSearchResult[]> {
  let q = supabase.from('products').select('id, name, price, image_url').eq('tenant_id', tenantId).eq('is_active', true).order('name').limit(20);
  const term = (query || '').trim();
  if (term.length >= 1) q = q.ilike('name', `%${term}%`);
  const { data, error } = await q;
  if (error) return [];
  return (data ?? []) as unknown as ProductSearchResult[];
}

/** Sugerencia de cliente existente para autocompletar al crear un pedido. */
export interface CustomerSuggestion {
  name: string;
  phone: string | null;
  email: string | null;
  doc_number: string | null;
  doc_type: string | null;
  last_order_id: string;
}

// Fila intermedia de la búsqueda/dedup de clientes (orders_v2 + perfil COD).
interface CustomerRow {
  id: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  doc_number: string | null;
  doc_type: string | null;
  created_at: string | null;
}

// Sanea el término para el filtro PostgREST `.or()` (comas/paréntesis/comodines rompen la sintaxis).
const sanitizeTerm = (s: string) => s.replace(/[,()*%]/g, ' ').trim();

// Clave de identidad del cliente para deduplicar: documento > teléfono > nombre+email.
function customerKey(r: { doc_number: string | null; phone: string | null; name: string | null; email: string | null }): string {
  const doc = (r.doc_number || '').trim();
  if (doc) return `doc:${doc}`;
  const phone = (r.phone || '').replace(/\D/g, '');
  if (phone) return `tel:${phone}`;
  return `nm:${(r.name || '').toLowerCase().trim()}|${(r.email || '').toLowerCase().trim()}`;
}

// PostgREST devuelve la relación 1:1 como objeto o (si no hay unique) array; lo normalizamos.
function unwrapCod(cod: unknown): { doc_number: string | null; doc_type: string | null } {
  const c = (Array.isArray(cod) ? cod[0] : cod) as { doc_number?: string | null; doc_type?: string | null } | null | undefined;
  return { doc_number: c?.doc_number ?? null, doc_type: c?.doc_type ?? null };
}

function toSuggestions(rows: CustomerRow[], limit: number): CustomerSuggestion[] {
  rows.sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
  const seen = new Set<string>();
  const out: CustomerSuggestion[] = [];
  for (const r of rows) {
    if (!r.name && !r.phone && !r.doc_number) continue;
    const key = customerKey(r);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ name: r.name ?? '', phone: r.phone, email: r.email, doc_number: r.doc_number, doc_type: r.doc_type, last_order_id: r.id });
    if (out.length >= limit) break;
  }
  return out;
}

// Trae nombre/contacto de una lista de perfiles COD (matches por documento).
async function rowsFromProfileIds(tenantId: string, profs: { order_id: string; doc_number: string | null; doc_type: string | null }[]): Promise<CustomerRow[]> {
  const ids = profs.map((p) => p.order_id);
  if (ids.length === 0) return [];
  const { data: ords } = await supabase
    .from('orders_v2')
    .select('id, customer_name, customer_phone, customer_email, created_at')
    .eq('tenant_id', tenantId)
    .in('id', ids);
  const profById = new Map(profs.map((p) => [p.order_id, p]));
  return ((ords ?? []) as Record<string, unknown>[]).map((o) => {
    const p = profById.get(o.id as string);
    return {
      id: o.id as string,
      name: (o.customer_name as string) ?? null,
      phone: (o.customer_phone as string) ?? null,
      email: (o.customer_email as string) ?? null,
      created_at: (o.created_at as string) ?? null,
      doc_number: p?.doc_number ?? null,
      doc_type: p?.doc_type ?? null,
    };
  });
}

/**
 * Busca clientes existentes por CUALQUIER dato único (nombre, teléfono, email o
 * documento) para el autocompletado estilo Odoo. Deduplica por identidad
 * (documento > teléfono > nombre+email) y ordena por recencia.
 */
export async function searchCustomers(tenantId: string, query: string): Promise<CustomerSuggestion[]> {
  const term = (query || '').trim();
  if (term.length < 2) return [];
  const safe = sanitizeTerm(term);
  if (!safe) return [];
  const digits = term.replace(/\D/g, '');
  const hasLetters = /\p{L}/u.test(term);

  // OR principal en orders_v2: nombre/email solo si hay letras; teléfono desde 2 dígitos.
  const ors: string[] = [];
  if (hasLetters) ors.push(`customer_name.ilike.%${safe}%`, `customer_email.ilike.%${safe}%`);
  if (digits.length >= 2) ors.push(`customer_phone.ilike.%${digits}%`);
  if (ors.length === 0) ors.push(`customer_name.ilike.%${safe}%`);

  // Documento por prefijo desde 2 chars, con el pedido EMBEBIDO (1 sola consulta → sin 2ª ola).
  const docByPrefix = safe.length >= 2
    ? supabase
        .from('order_delivery_profile')
        .select('doc_number, doc_type, orders_v2(id, customer_name, customer_phone, customer_email, created_at)')
        .eq('tenant_id', tenantId)
        .ilike('doc_number', `${safe}%`)
        .limit(20)
    : Promise.resolve({ data: [] as Record<string, unknown>[] });

  // Principal y documento EN PARALELO (una sola ola de red).
  const [mainRes, docRes] = await Promise.all([
    supabase
      .from('orders_v2')
      .select('id, customer_name, customer_phone, customer_email, created_at, cod:order_delivery_profile(doc_number, doc_type)')
      .eq('tenant_id', tenantId)
      .or(ors.join(','))
      .order('created_at', { ascending: false })
      .limit(40),
    docByPrefix,
  ]);

  const rows: CustomerRow[] = [];
  for (const r of (mainRes.data ?? []) as Record<string, unknown>[]) {
    rows.push({
      id: r.id as string, name: (r.customer_name as string) ?? null, phone: (r.customer_phone as string) ?? null,
      email: (r.customer_email as string) ?? null, created_at: (r.created_at as string) ?? null, ...unwrapCod(r.cod),
    });
  }
  for (const r of (docRes.data ?? []) as Record<string, unknown>[]) {
    const o = (Array.isArray(r.orders_v2) ? r.orders_v2[0] : r.orders_v2) as Record<string, unknown> | null | undefined;
    if (!o) continue;
    rows.push({
      id: o.id as string, name: (o.customer_name as string) ?? null, phone: (o.customer_phone as string) ?? null,
      email: (o.customer_email as string) ?? null, created_at: (o.created_at as string) ?? null,
      doc_number: (r.doc_number as string) ?? null, doc_type: (r.doc_type as string) ?? null,
    });
  }

  return toSuggestions(rows, 8);
}

/** Clientes recientes (sin término de búsqueda) para mostrar el desplegable al hacer foco. */
export async function fetchRecentCustomers(tenantId: string, limit = 8): Promise<CustomerSuggestion[]> {
  const { data } = await supabase
    .from('orders_v2')
    .select('id, customer_name, customer_phone, customer_email, created_at, cod:order_delivery_profile(doc_number, doc_type)')
    .eq('tenant_id', tenantId)
    .not('customer_name', 'is', null)
    .order('created_at', { ascending: false })
    .limit(60);
  const rows: CustomerRow[] = [];
  for (const r of (data ?? []) as Record<string, unknown>[]) {
    rows.push({
      id: r.id as string, name: (r.customer_name as string) ?? null, phone: (r.customer_phone as string) ?? null,
      email: (r.customer_email as string) ?? null, created_at: (r.created_at as string) ?? null, ...unwrapCod(r.cod),
    });
  }
  return toSuggestions(rows, limit);
}

/**
 * Detecta clientes existentes que comparten un dato único (documento, teléfono o
 * email) con los valores dados, excluyendo el pedido actual. Base del aviso de
 * duplicados estilo Odoo antes de crear/guardar.
 */
export async function findCustomerDuplicates(
  tenantId: string,
  fields: { phone?: string | null; docNumber?: string | null; email?: string | null },
  excludeOrderId: string,
): Promise<CustomerSuggestion[]> {
  const phone = (fields.phone || '').replace(/\D/g, '');
  const doc = (fields.docNumber || '').trim();
  const email = (fields.email || '').trim().toLowerCase();
  if (!phone && !doc && !email) return [];
  const rows: CustomerRow[] = [];

  if (phone.length >= 6 || email) {
    const ors: string[] = [];
    if (phone.length >= 6) ors.push(`customer_phone.ilike.%${phone}%`);
    if (email) ors.push(`customer_email.ilike.${sanitizeTerm(email)}`);
    if (ors.length > 0) {
      const { data } = await supabase
        .from('orders_v2')
        .select('id, customer_name, customer_phone, customer_email, created_at, cod:order_delivery_profile(doc_number, doc_type)')
        .eq('tenant_id', tenantId)
        .neq('id', excludeOrderId)
        .or(ors.join(','))
        .order('created_at', { ascending: false })
        .limit(20);
      for (const r of (data ?? []) as Record<string, unknown>[]) {
        rows.push({
          id: r.id as string, name: (r.customer_name as string) ?? null, phone: (r.customer_phone as string) ?? null,
          email: (r.customer_email as string) ?? null, created_at: (r.created_at as string) ?? null, ...unwrapCod(r.cod),
        });
      }
    }
  }

  if (doc) {
    const { data: profs } = await supabase
      .from('order_delivery_profile')
      .select('order_id, doc_number, doc_type')
      .eq('tenant_id', tenantId)
      .eq('doc_number', doc)
      .neq('order_id', excludeOrderId)
      .limit(20);
    rows.push(...await rowsFromProfileIds(tenantId, (profs ?? []) as { order_id: string; doc_number: string | null; doc_type: string | null }[]));
  }

  return toSuggestions(rows, 5);
}

/**
 * Costos unitarios de productos, para el Margen (best-effort: ante un error devuelve {}).
 *
 * 🪤 POR LA FUNCIÓN, NO POR LA COLUMNA (20260929160000): `products.cost` ya no se lee en crudo. La
 *    función devuelve los costos sólo a quien los ve —el vendedor recibe una lista vacía—.
 */
export async function fetchProductCosts(tenantId: string, productIds: (string | null)[]): Promise<Record<string, number>> {
  const ids = Array.from(new Set(productIds.filter(Boolean) as string[]));
  if (ids.length === 0) return {};
  const { data, error } = await supabase.rpc('fn_costos_de_productos', { p_tenant: tenantId, p_ids: ids });
  if (error) return {};
  const map: Record<string, number> = {};
  for (const r of (data ?? []) as { id: string; cost: number | null }[]) map[r.id] = Number(r.cost || 0);
  return map;
}

/** Actualiza el costo unitario de un producto (best-effort; se ignora si la columna `cost` no existe aún). */
export async function updateProductCost(tenantId: string, productId: string, cost: number): Promise<void> {
  await supabase.from('products').update({ cost }).eq('tenant_id', tenantId).eq('id', productId);
}

/** Imágenes de productos por id (para thumbnails de líneas). Devuelve id→image_url. */
export async function fetchProductImages(tenantId: string, productIds: (string | null)[]): Promise<Record<string, string>> {
  const ids = Array.from(new Set(productIds.filter(Boolean) as string[]));
  if (ids.length === 0) return {};
  const { data, error } = await supabase.from('products').select('id, image_url').eq('tenant_id', tenantId).in('id', ids);
  if (error) return {};
  const map: Record<string, string> = {};
  for (const r of (data ?? []) as { id: string; image_url: string | null }[]) if (r.image_url) map[r.id] = r.image_url;
  return map;
}

/** Líneas de un pedido. Resiliente a `display_type` ausente (migración no aplicada aún). */
export async function fetchOrderLines(tenantId: string, orderId: string): Promise<OrderLine[]> {
  const base = 'id, order_id, tenant_id, product_id, name, qty, unit_price, discount, line_total, image_url, sku';
  const run = (cols: string) => supabase.from('order_lines').select(cols).eq('tenant_id', tenantId).eq('order_id', orderId);
  let res = await run(`${base}, display_type`);
  if (res.error && /display_type/.test(res.error.message)) res = await run(base);
  if (res.error) throw res.error;
  return (res.data ?? []) as unknown as OrderLine[];
}

// ── Chatter: actividades (recordatorios de seguimiento estilo Odoo) ─────────
export type ActivityKind = 'call' | 'whatsapp' | 'followup' | 'other';
export interface OrderActivity {
  id: string;
  order_id: string;
  tenant_id: string;
  kind: ActivityKind;
  note: string | null;
  due_date: string | null;
  done: boolean;
  created_at: string;
  /** Quién la programó. */
  created_by?: string | null;
}

export async function fetchOrderActivities(tenantId: string, orderId: string): Promise<OrderActivity[]> {
  const { data, error } = await supabase
    .from('order_activities')
    .select('id, order_id, tenant_id, kind, note, due_date, done, created_at, created_by')
    .eq('tenant_id', tenantId)
    .eq('order_id', orderId)
    .order('done', { ascending: true })
    .order('due_date', { ascending: true, nullsFirst: false });
  if (error) return [];
  return (data ?? []) as unknown as OrderActivity[];
}

export async function addOrderActivity(order: OrderRecord, input: { kind: ActivityKind; note: string; due_date: string | null }): Promise<void> {
  const { error } = await supabase.from('order_activities').insert({
    order_id: order.id, tenant_id: order.tenant_id,
    kind: input.kind, note: input.note || null, due_date: input.due_date || null,
  });
  if (error) throw error;
}

export async function setActivityDone(tenantId: string, id: string, done: boolean): Promise<void> {
  const { error } = await supabase.from('order_activities').update({ done }).eq('id', id).eq('tenant_id', tenantId);
  if (error) throw error;
}

/** Agrega una nota interna del asesor al timeline (vía RPC SECURITY DEFINER). */
export async function addOrderNote(orderId: string, text: string): Promise<void> {
  const { error } = await supabase.rpc('fn_order_add_note', { p_order_id: orderId, p_text: text });
  if (error) throw error;
}

/** Pedido anterior/siguiente dentro de la misma etapa (navegación ‹ › del detalle). */
export async function fetchAdjacentOrders(tenantId: string, order: OrderRecord): Promise<{ prevId: string | null; nextId: string | null }> {
  const base = () => supabase.from('orders_v2').select('id').eq('tenant_id', tenantId).eq('lifecycle_stage', order.lifecycle_stage);
  const [newer, older] = await Promise.all([
    base().gt('created_at', order.created_at).order('created_at', { ascending: true }).limit(1),
    base().lt('created_at', order.created_at).order('created_at', { ascending: false }).limit(1),
  ]);
  const pick = (r: { data: { id: string }[] | null }) => r.data?.[0]?.id ?? null;
  return { prevId: pick(newer), nextId: pick(older) };
}

export interface CustomerStats {
  count: number;
  totalSpent: number;
  delivered: number;
  failed: number;
  recent: { id: string; order_number: string; grand_total: number; created_at: string; lifecycle_stage: string }[];
}

/** Histórico del cliente por teléfono (Cliente 360 + base del riesgo COD). Excluye el pedido actual. */
export async function fetchCustomerStats(tenantId: string, phone: string | null, excludeId: string): Promise<CustomerStats | null> {
  if (!phone) return null;
  const { data, error } = await supabase
    .from('orders_v2')
    .select('id, order_number, grand_total, created_at, lifecycle_stage')
    .eq('tenant_id', tenantId)
    .eq('customer_phone', phone)
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) return null;
  const rows = ((data ?? []) as CustomerStats['recent'][number][]).filter((r) => r.id !== excludeId);
  const isSale = (s: string) => s === 'sale' || s === 'fulfilled' || s === 'closed';
  return {
    /*
     * 🪤 UNA COMPRA ES UNA VENTA CONFIRMADA. Esto contaba TODAS las filas, así que sumaba
     * presupuestos sin confirmar y pedidos cancelados: la ficha de un cliente con una sola compra
     * decía «16» porque cada click en "Crear venta" dejaba un borrador vacío. Un número inventado en
     * el lugar donde el vendedor decide cómo tratar a la persona que tiene enfrente.
     */
    count: rows.filter((r) => isSale(r.lifecycle_stage)).length,
    totalSpent: rows.filter((r) => isSale(r.lifecycle_stage)).reduce((s, r) => s + Number(r.grand_total || 0), 0),
    delivered: rows.filter((r) => r.lifecycle_stage === 'fulfilled' || r.lifecycle_stage === 'closed').length,
    failed: rows.filter((r) => r.lifecycle_stage === 'cancelled').length,
    recent: rows.slice(0, 5),
  };
}

/**
 * Aplica una transición de estado vía la state machine en BD (única vía válida).
 * Devuelve la nueva etapa. Lanza si la transición no está permitida.
 */
export async function transitionOrder(orderId: string, event: OrderTransitionEvent, actor?: string, reason?: string): Promise<OrderLifecycleStage> {
  // 🚨 `p_reason` viaja: la base exige motivo para cancelar y el panel no lo mandaba (cancelarLlevaElMotivo.test.ts).
  const { data, error } = await supabase.rpc('fn_order_transition', { p_order_id: orderId, p_event: event, p_actor: actor ?? null, p_reason: reason ?? null });
  if (error) throw error;

  /*
   * ⭐ CONFIRMAR UNA VENTA DE SMARTY DROP MUEVE PLATA, y quien la movió fue la BASE: al confirmar,
   *    `fn_drop_la_venta_confirmada_pide_sola` le pide el producto al almacén y lo cobra del saldo.
   *    El panel no se entera de nada de eso —no hizo la escritura— así que la billetera y la tarjeta
   *    de la venta seguirían mostrando los números de antes hasta que alguien recargue la página.
   *
   * 🪤 SE INVALIDA ACÁ Y NO EN CADA PANTALLA. Confirmar se puede hacer desde la lista de Ventas,
   *    desde la venta abierta y desde una selección múltiple. Repetir esto en los tres es la forma
   *    exacta en que este repo viene rompiendo reglas: se arregla en dos y el tercero queda viejo.
   *
   * 🪤 Y SÓLO EN `confirm`. Cancelar también devuelve saldo, pero por otro camino y con su propia
   *    recarga de datos; invalidar en toda transición sería pedirle consultas de más a pantallas que
   *    no cambiaron.
   */
  if (event === 'confirm') olvidarLoDeLaBilleteraDrop();

  return data as OrderLifecycleStage;
}

export interface OrderPayment {
  id: string;
  tenant_id: string;
  order_id: string;
  kind: string;
  method: string;
  amount: number;
  notes: string | null; // referencia / código de operación
  created_at: string;
  /** Quién registró el pago (auth.uid()); null = Smarty (pasarela). 20261026180000. */
  actor_id?: string | null;
}

export interface ShipmentEvent {
  status: string;
  at: string;
  note?: string | null;
  /** Quién lo hizo (auth.uid(), lo firma la base; una edge function a nombre de alguien lo pasa). Sin `by` = Smarty. 20261026180000. */
  by?: string | null;
}

export interface OrderShipment {
  id: string;
  tenant_id: string;
  order_id: string;
  carrier: string | null;
  /** Código de orden Shalom (4 caracteres, letras tipo "MDNN"). Con N° de orden + este código se rastrea el envío. */
  agency_ref: string | null;
  /** Nombre legible de la agencia de destino (para mostrar en Entrega). */
  agency_name: string | null;
  tracking_number: string | null;
  /** Clave secreta que el cliente digita al recoger en agencia. */
  secret_code: string | null;
  /** Tamaño de caja Shalom usado (nombre legible, ej. "Caja Paquete XXS"). NULL = default del tenant. */
  package_size: string | null;
  /**
   * Servicio/courier concreto DENTRO del proveedor de `carrier`. En Aliclik es el courier que eligió
   * el operador al registrar (Aliclik despacha con varios). Es lo que Smarty PIDIÓ: su API no
   * devuelve el courier en ninguna consulta posterior, así que la ficha lo rotula como tal.
   */
  service_name?: string | null;
  /** Modo del servicio: 'normal' | 'express' (Aliclik express = sobrecosto + entrega el mismo día). */
  service_mode?: string | null;
  /** Por qué portal de Shalom salió la guía ('pro' | 'corporativo') y con la cuenta de quién: los estampa el registro. */
  carrier_portal?: string | null;
  cuenta_tenant_id?: string | null;
  /** La cuenta PERSONAL de Shalom (de un vendedor) con la que salió; null = la del negocio (22-oct-2026). */
  cuenta_user_id?: string | null;
  /** Logo del courier tal como lo cotizó el proveedor. Sólo presentación: si no carga, se oculta. */
  service_logo_url?: string | null;
  /**
   * Cómo llega el paquete: 'domicilio' (el courier lo lleva a la puerta) o 'agencia' (el cliente lo
   * recoge con clave). En Aliclik decide el endpoint de creación Y el de anulación, que son
   * distintos. NULL = envío anterior a la columna o cargado a mano; se trata como domicilio.
   */
  delivery_mode?: string | null;
  status: string;
  /**
   * `'smarty'` si la guía la emitió NUESTRA integración con el transportista.
   *
   * ⭐ Decide si se ofrece editar los datos del envío a mano. Cuando la guía salió de la API, el
   * número, la clave de agencia y la clave de seguridad son la verdad del transportista: editarlos
   * rompe el vínculo y el rastreo deja de encontrar el envío, sin ningún error visible.
   *
   * 🪤 `null` NO significa «externo», significa «no consta». Las filas anteriores a esta columna
   * —incluida toda la importación de Odoo— lo tienen vacío, y por eso siguen siendo editables: es
   * la opción segura de las dos, porque esconder el lápiz donde sí hace falta deja al operador sin
   * forma de anotar una guía.
   */
  registered_via?: string | null;
  /** Rótulo (guía imprimible) en Storage — hoy Olva. Se abre/imprime desde Entrega. */
  label_url?: string | null;
  /**
   * La agencia de origen de la guía, congelada al emitirla: la que se le mandó al transportista (2-nov-2026). Con la
   * guía emitida, Entrega muestra ésta y no la del almacén de hoy (que puede haberse mudado).
   */
  origin_agency_id?: string | null;
  origin_agency_name?: string | null;
  events?: ShipmentEvent[];
  created_at: string;
  /**
   * Modalidad del courier: `motorizado` entrega en la puerta y cobra; `agency` es recojo en
   * agencia y la agencia NO cobra. Sale de `logistics_couriers.type`.
   *
   * 🪤 NO se deduce de `agency_name`: ese campo está NULL en 15.398 de 15.402 envíos, así que la
   * lógica que dependía de él daba "domicilio" siempre — y el rótulo de 9.630 pedidos de Shalom
   * salía con "COBRAR EN ENTREGA" a una agencia que no cobra.
   */
  courier_type?: 'motorizado' | 'agency' | null;
  /** Caché del último rastreo Shalom { search, statuses } para render instantáneo del modal de estado. */
  last_tracking?: unknown;
  last_tracked_at?: string | null;
  /** CDN de la tarjeta de rastreo ya dibujada. La lee cualquiera — incluido el agente. */
  rastreo_imagen_url?: string | null;
  /** Con qué estado se dibujó. Si no coincide con el actual, quedó vieja. */
  rastreo_imagen_version?: string | null;
}

export async function fetchOrderPayments(tenantId: string, orderId: string): Promise<OrderPayment[]> {
  const { data, error } = await supabase
    .from('order_payments')
    .select('id, tenant_id, order_id, kind, method, amount, notes, created_at, actor_id')
    .eq('tenant_id', tenantId)
    .eq('order_id', orderId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as OrderPayment[];
}

/**
 * Cuánto le cobra Shalom al cliente con Cobro Seguro en esta venta (`order_shipments.cobro_seguro_monto`), o null.
 *
 * 🪤 VA APARTE DE `fetchOrderShipments` A PROPÓSITO: la columna nace en la migración 20261021150000. Si el panel se
 *    publica antes que la migración, sumarla a esa consulta rompería la página de la venta entera; acá, un error
 *    sólo significa «sin Cobro Seguro».
 */
export async function fetchCobroSeguroDeLaVenta(orderId: string): Promise<number | null> {
  const { data, error } = await supabase
    .from('order_shipments').select('cobro_seguro_monto')
    .eq('order_id', orderId).not('tracking_number', 'is', null);
  if (error) return null;
  const montos = ((data ?? []) as Array<{ cobro_seguro_monto: number | null }>)
    .map((f) => Number(f.cobro_seguro_monto ?? 0)).filter((n) => n > 0);
  return montos.length ? Math.max(...montos) : null;
}

// Del PAQUETE (plan un paquete, F1): la venta y su encargo leen la misma fila; la base decide qué se ve.
export async function fetchOrderShipments(_tenantId: string, orderId: string): Promise<OrderShipment[]> {
  const { data, error } = await supabase
    .rpc('fn_envios_del_pedido', { p_order: orderId })
    .select('id, tenant_id, order_id, carrier, agency_ref, agency_name, tracking_number, secret_code, package_size, service_name, service_mode, service_logo_url, delivery_mode, status, events, created_at, last_tracking, last_tracked_at, registered_via, label_url, rastreo_imagen_url, rastreo_imagen_version, carrier_portal, cuenta_tenant_id, cuenta_user_id, origin_agency_id, origin_agency_name')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as OrderShipment[];
}

/*
 * 🪤 «SALDO» ES UN MÉTODO DE VERDAD, no un `transfer` disfrazado. Cuando un pedido de Smarty Drop
 * se paga con el anticipo del revendedor, la plata no entró por transferencia ese día: entró antes,
 * cuando se confirmó la recarga. Escribir el método equivocado para no tocar un CHECK es cómo se
 * construye un reporte que miente — mañana alguien concilia las transferencias con el banco y le
 * sobra plata que nunca pasó por ahí.
 */
export type PaymentMethod = 'cod' | 'cash' | 'transfer' | 'yape' | 'plin' | 'gateway' | 'saldo' | 'al_entregar' | 'cobro_seguro';

export const PAYMENT_METHOD_LABEL: Record<PaymentMethod, string> = {
  cod: 'Contraentrega', cash: 'Efectivo', transfer: 'Transferencia', yape: 'Yape', plin: 'Plin', gateway: 'Pasarela',
  // El pedido se pagó con el anticipo que el revendedor ya le había depositado al proveedor.
  saldo: 'Saldo Smarty Drop', al_entregar: 'Al entregar', // dada por cobrada al entregarse (Ventas › Configuración)
  cobro_seguro: 'Cobro Seguro (Shalom)', // lo cobró Shalom en la agencia y se lo deposita al drop (20-oct-2026)
};

/**
 * Registra un cobro: SOLO inserta la transacción. El trigger
 * fn_order_payments_recalc recalcula amount_paid/payment_status/collection_status
 * (server-authoritative). El kind se infiere del estado de pago actual.
 */
export async function registerPayment(
  order: OrderRecord,
  input: { amount: number; method: PaymentMethod; reference?: string | null; paidAt?: string | null; proofUrl?: string | null },
): Promise<void> {
  const kind = inferPaymentKind(Number(order.amount_paid || 0), input.amount, Number(order.grand_total || 0));
  const row: Record<string, unknown> = {
    order_id: order.id,
    tenant_id: order.tenant_id,
    kind,
    method: input.method,
    amount: input.amount,
    notes: input.reference?.trim() || null,
    /*
     * ⭐ EL CÓDIGO DE OPERACIÓN TAMBIÉN VA A UN CAMPO ESTRUCTURADO.
     *
     * 🚨 Hasta acá vivía SÓLO en `notes`, que es texto libre —hay 20.421 pagos y sus notas dicen
     * cosas como «Odoo · PYAPE/2026/00857»—, mientras `gateway_ref` estaba vacío en los 20.421. O
     * sea que el número con el que se identifica un pago en el banco no estaba en ningún lado
     * consultable: no se podía buscar un pago por su código ni detectar el mismo comprobante
     * enviado dos veces.
     *
     * 🪤 Se escribe en LOS DOS. `notes` se sigue mostrando en la tabla de Pagos y en la cronología;
     * cambiarlo de lugar habría vaciado esas dos pantallas para todo lo nuevo. Duplicar un dato
     * corto es más barato que migrar dos vistas y 20 mil filas por una mejora que recién empieza.
     */
    gateway_ref: input.reference?.trim() || null,
  };
  // `paidAt` es una fecha (YYYY-MM-DD). Solo back-fechamos si el usuario eligió un día PASADO;
  // si es hoy, NO tocamos created_at para que el DB use now() (hora real del registro). Antes se
  // convertía la fecha a medianoche UTC → se veía a las 19:00 (Lima) en vez de la hora real.
  if (input.paidAt) {
    const now = new Date();
    const localToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    if (input.paidAt < localToday) row.created_at = new Date(`${input.paidAt}T12:00:00`).toISOString();
  }
  if (input.proofUrl) row.proof_url = input.proofUrl; // requiere migración payment_proof
  const { error } = await supabase.from('order_payments').insert(row);
  if (error) throw error;
}

/** Sube la captura/comprobante de un pago a R2 y devuelve su URL pública (CDN). */
export async function uploadPaymentProof(tenantId: string, orderId: string, file: File): Promise<string> {
  const { url } = await uploadAsset(file, {
    category: 'documents',
    identifier: orderId,
    tenantId,
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'],
    maxBytes: 25 * 1024 * 1024,
  });
  return url;
}

/** Comprobantes de pago por id (best-effort: {} si la columna proof_url no existe aún). */
export async function fetchPaymentProofs(tenantId: string, orderId: string): Promise<Record<string, string>> {
  const { data, error } = await supabase.from('order_payments').select('id, proof_url').eq('tenant_id', tenantId).eq('order_id', orderId);
  if (error) return {};
  const map: Record<string, string> = {};
  for (const r of (data ?? []) as { id: string; proof_url: string | null }[]) if (r.proof_url) map[r.id] = r.proof_url;
  return map;
}

/** Registra un reembolso (kind='refund'). El trigger pone payment_status='refunded'. */
export async function refundPayment(
  order: OrderRecord,
  input: { amount: number; method: PaymentMethod; notes?: string },
): Promise<void> {
  const { error } = await supabase.from('order_payments').insert({
    order_id: order.id,
    tenant_id: order.tenant_id,
    kind: 'refund',
    method: input.method,
    amount: input.amount,
    notes: input.notes ?? null,
  });
  if (error) throw error;
}

/** Elimina un pago/reembolso del pedido. El trigger trg_order_payments_recalc (AFTER DELETE)
 *  recalcula amount_paid/amount_due/estado automáticamente. */
export async function deletePayment(tenantId: string, paymentId: string): Promise<void> {
  const { error } = await supabase.from('order_payments').delete().eq('tenant_id', tenantId).eq('id', paymentId);
  if (error) throw error;
}

/** Edita la cobranza COD: promesa de pago, notas y estado (incl. 'waived'). */
export async function updateCollection(
  order: OrderRecord,
  patch: Partial<{ commitment_date: string | null; commitment_notes: string | null; collection_status: string }>,
): Promise<void> {
  const { error } = await supabase
    .from('order_delivery_profile')
    .update(patch)
    .eq('order_id', order.id)
    .eq('tenant_id', order.tenant_id);
  if (error) throw error;
}

export interface NewOrderLineInput {
  name: string;
  qty: number;
  unit_price: number;
  product_id?: string | null;
}

export interface NewOrderCodInput {
  department?: string;
  province?: string;
  district?: string;
  address?: string;
  address_ref?: string;
  doc_type?: string;
  doc_number?: string;
  commitment_date?: string | null;
  expected_advance?: number;
  expected_balance?: number;
  /**
   * Pin heredado de la ficha del cliente. Viaja con la ubicación porque sin él Aliclik exige
   * re-ubicar al cliente en el mapa en CADA venta, aunque su dirección ya esté guardada.
   */
  geo_lat?: number | null;
  geo_lng?: number | null;
}

export interface NewOrderInput {
  customer_name: string;
  customer_phone?: string;
  customer_email?: string;
  channel?: OrderRecord['channel'];
  is_cod?: boolean;
  shipping_total?: number;
  currency?: string;
  lines: NewOrderLineInput[];
  cod?: NewOrderCodInput | null;
  /** Comercial (vendedor) por defecto = quien crea la venta, si es miembro de operaciones. */
  salesperson_id?: string | null;
  sales_team_id?: string | null;
}

/**
 * Crea un pedido en orders_v2 como cotización (etapa 'quotation' → entra a
 * Pedidos para confirmarse). Calcula los totales desde las líneas (no hay
 * trigger que los sume) — el número de pedido y amount_due los derivan los
 * triggers. Mismo punto de entrada que reutilizará el cutover del formulario COD.
 */
export async function createOrder(tenantId: string, input: NewOrderInput): Promise<string> {
  const lines = input.lines.map((l) => ({ ...l, line_total: Number((l.qty * l.unit_price).toFixed(2)) }));
  const subtotal = Number(lines.reduce((s, l) => s + l.line_total, 0).toFixed(2));
  const shipping_total = Number(input.shipping_total ?? 0);
  const grand_total = Number((subtotal + shipping_total).toFixed(2));

  const { data: order, error } = await supabase
    .from('orders_v2')
    .insert({
      tenant_id: tenantId,
      lifecycle_stage: 'quotation',
      channel: input.channel ?? 'manual',
      is_cod: input.is_cod ?? false,
      customer_name: input.customer_name || null,
      customer_phone: input.customer_phone || null,
      customer_email: input.customer_email || null,
      currency: input.currency ?? 'PEN',
      subtotal,
      shipping_total,
      grand_total,
      amount_paid: 0,
      payment_status: 'unpaid',
      shipping_status: 'none',
      ...(input.salesperson_id ? { salesperson_id: input.salesperson_id } : {}),
      ...(input.sales_team_id ? { sales_team_id: input.sales_team_id } : {}),
    })
    .select('id')
    .single();
  if (error) throw error;
  const orderId = (order as { id: string }).id;

  /*
   * ⚡ LOS DOS HIJOS SE INSERTAN EN PARALELO. Ninguno depende del otro —los dos sólo necesitan el
   * pedido, que ya está creado— y encadenarlos costaba un viaje de red entero por nada. La base
   * resuelve cada insert en microsegundos; lo que se siente es la ida y vuelta.
   */
  // 🪤 `PromiseLike` y no `Promise`: el builder de PostgREST es un thenable, no una Promise.
  const tareas: PromiseLike<{ error: unknown }>[] = [];

  if (lines.length > 0) {
    /*
     * ⭐ POR LA PUERTA ÚNICA, EN LOTE. Ver ADR 0044. Antes esto armaba las filas a mano, que es
     *    exactamente lo que hacía cada uno de los cinco escritores — y por eso la regla se rompía
     *    una vez por escritor.
     *
     * 🪤 EN LOTE Y NO LÍNEA POR LÍNEA: mandar cada una por separado cambiaría UN viaje de red por
     *    N, y este insert está en paralelo con el perfil de entrega justamente para no pagar dos.
     *    `line_total` no se manda: lo calcula `trg_order_line_total` en la base.
     */
    tareas.push(supabase.rpc('fn_order_lines_guardar_lote', {
      p_order_id: orderId,
      p_lineas: lines.map((l) => ({
        product_id: l.product_id ?? null,
        name: l.name,
        qty: l.qty,
        unit_price: l.unit_price,
        discount: 0,
      })),
    }) as unknown as PromiseLike<{ error: unknown }>);
  }

  const cod = input.cod;
  const hasCod = cod && (cod.address || cod.department || cod.district || cod.doc_number);
  if (hasCod) {
    tareas.push(supabase.from('order_delivery_profile').insert({
      order_id: orderId,
      tenant_id: tenantId,
      department: cod.department || null,
      province: cod.province || null,
      district: cod.district || null,
      address: cod.address || null,
      address_ref: cod.address_ref || null,
      doc_type: cod.doc_type || null,
      doc_number: cod.doc_number || null,
      commitment_date: cod.commitment_date || null,
      expected_advance: cod.expected_advance ?? 0,
      expected_balance: cod.expected_balance ?? 0,
      geo_lat: cod.geo_lat ?? null,
      geo_lng: cod.geo_lng ?? null,
    }));
  }

  for (const { error: err } of await Promise.all(tareas)) {
    if (err) throw err;
  }

  return orderId;
}

// ── O1: edición de pedido (los totales los recalculan los triggers de la BD) ──

/** Agrega una línea a un pedido. line_total y los totales del pedido los deriva la BD. */
/**
 * ⭐ ESCRIBE POR LA PUERTA ÚNICA (`fn_order_line_guardar`), no con un insert propio. Ver ADR 0044.
 *
 *    Cinco caminos escriben líneas de pedido y cada uno las armaba a mano; por eso la regla se
 *    rompía una vez por camino — ya pasó en el webhook de Shopify y volvió a pasar acá. La función
 *    vive en la base porque es el único lugar al que llegan los tres runtimes del sistema.
 */
export async function addOrderLine(
  order: OrderRecord,
  line: { name: string; qty: number; unit_price: number; discount?: number; product_id?: string | null; display_type?: 'note' | 'section' | null },
): Promise<void> {
  const { error } = await supabase.rpc('fn_order_line_guardar', {
    p_order_id: order.id,
    p_line_id: null,
    p_product_id: line.product_id ?? null,
    p_qty: line.qty,
    p_unit_price: line.unit_price,
    p_discount: line.discount ?? 0,
    p_name: line.name,
    p_display_type: line.display_type ?? null,
  });
  if (error) throw error;
}

/** Edita una línea (cantidad/precio/descuento/nombre). */
/**
 * 🚨 `product_id` FALTABA EN ESTE PATCH, y era el bug. Cambiar el producto de una línea YA EXISTENTE
 *    actualizaba el nombre y el precio, y dejaba el vínculo apuntando al producto viejo — sin error,
 *    sin aviso, y con la fila mostrando el nombre nuevo.
 *
 *    Pedido S-16671, 3-sep-2026: la línea decía «DuroMax Vigorizante Natural Frasco Mediano» a
 *    S/ 89 —el nombre y el precio de DuroMax— y su `product_id` seguía siendo el de «Alphaman
 *    Americano». Lo delataba la FOTO, que se lee del producto vinculado. Y abajo,
 *    `trg_order_lines_move_stock` descuenta por `product_id`: la venta le sacó stock a Alphaman.
 *
 * 🪤 Agregar una línea SÍ escribía el vínculo (`addOrderLine`). Por eso el defecto sólo aparecía al
 *    CORREGIR una línea, que es exactamente lo que se hace cuando uno se equivocó de producto.
 */
export async function updateOrderLine(
  order: OrderRecord,
  lineId: string,
  linea: { name: string; qty: number; unit_price: number; discount: number; product_id: string | null; display_type?: 'note' | 'section' | null },
): Promise<void> {
  /*
   * 🪤 LA PUERTA RECIBE LA LÍNEA ENTERA, NO UN `patch`. Un patch parcial es justo lo que permitió el
   *    bug de S-16671: se mandaba nombre y precio, se omitía `product_id`, y el vínculo se quedaba
   *    apuntando al producto viejo sin que nada fallara. Con la línea completa, omitir un campo deja
   *    de ser una posibilidad.
   */
  const { error } = await supabase.rpc('fn_order_line_guardar', {
    p_order_id: order.id,
    p_line_id: lineId,
    p_product_id: linea.product_id,
    p_qty: linea.qty,
    p_unit_price: linea.unit_price,
    p_discount: linea.discount,
    p_name: linea.name,
    p_display_type: linea.display_type ?? null,
  });
  if (error) throw error;
}

/** Elimina una línea. */
export async function removeOrderLine(tenantId: string, lineId: string): Promise<void> {
  const { error } = await supabase.from('order_lines').delete().eq('id', lineId).eq('tenant_id', tenantId);
  if (error) throw error;
}

/** Edita cargos a nivel pedido (descuento, envío). grand_total/amount_due los deriva la BD. */
export async function updateOrderCharges(
  order: OrderRecord,
  charges: Partial<{ discount_total: number; shipping_total: number; tax_total: number }>,
): Promise<void> {
  const { error } = await supabase.from('orders_v2').update(charges).eq('id', order.id).eq('tenant_id', order.tenant_id);
  if (error) throw error;
}

/** Elimina un registro de envío del pedido (order_shipments). Para "quitar el envío" desde las líneas. */
export async function deleteShipment(tenantId: string, shipmentId: string): Promise<void> {
  const { error } = await supabase.from('order_shipments').delete().eq('tenant_id', tenantId).eq('id', shipmentId);
  if (error) throw error;
}

/** Edita el flete: costo REAL del courier y quién lo paga (origen/destino). El margen lo deriva la UI. */
export async function updateFreight(
  order: OrderRecord,
  patch: Partial<{ shipping_cost: number; freight_payer: 'origin' | 'destination' }>,
): Promise<void> {
  const { error } = await supabase.from('orders_v2').update(patch).eq('id', order.id).eq('tenant_id', order.tenant_id);
  if (error) throw error;
}

/** Edita notas internas y etiquetas del pedido. */
export async function updateOrderNotesTags(
  order: OrderRecord,
  patch: Partial<{ notes: string | null; tags: string[] }>,
): Promise<void> {
  const { error } = await supabase.from('orders_v2').update(patch).eq('id', order.id).eq('tenant_id', order.tenant_id);
  if (error) throw error;
}

/**
 * Guarda la agencia de destino ELEGIDA, sin registrar nada todavía.
 *
 * 🚨 POR QUÉ EXISTE. La agencia se PRESELECCIONABA sola —la que ya tiene el envío, la habitual del
 *    cliente, o la primera de la lista— y esa preselección se ve idéntica a una decisión: aparece
 *    marcada en pantalla. Pero vivía sólo en el estado de React: no se escribía hasta apretar
 *    «Registrar envío».
 *
 *    Medido el 3-sep: de los tres pedidos de Smarty Drop, los tres tenían `carrier = Shalom` y
 *    NINGUNO agencia. El revendedor creía haber elegido, el almacén no recibía nada, y ninguno de
 *    los dos tenía cómo darse cuenta — la pantalla se veía igual en los dos casos.
 *
 * 🚨 SE ESCRIBE SÓLO `agency_name`, Y LA PRIMERA VERSIÓN ESTUVO MAL. Escribía también `agency_ref`
 *    con el id interno de la agencia, razonando que era «el campo que usa el registro». No lo es:
 *    el registro resuelve la agencia por NOMBRE contra el catálogo de Shalom Corporativo
 *    (`shalom_agency_name` / `shalom_agency_id`), y `agency_ref` es otra cosa —la CLAVE DE AGENCIA
 *    de 4 letras que devuelve el courier al emitir la guía, la que el cliente presenta en el
 *    mostrador—. El founder lo vio en pantalla: «la clave de agencia sale un código largo de varios
 *    dígitos». Era un UUID donde tenía que decir CTCC.
 *
 * 🪤 Y LA PRESELECCIÓN NUNCA NECESITÓ ESE CAMPO: el selector busca la agencia por `agency_name`
 *    (ver `EntregaShipmentPicker`, «yaPuesta»). O sea que el id interno no servía para nada y
 *    encima tapaba un dato que sí se usa. Dos significados en una columna es peor que dos columnas.
 *
 * No agrega evento al timeline: elegir no es un hecho del envío, es una decisión previa.
 */
export async function guardarAgenciaElegida(
  order: OrderRecord,
  agencia: { id: string; name: string } | null,
): Promise<void> {
  // Sin envío todavía no hay dónde guardarla; se guarda al crearlo. Por la puerta: ver `elEnvioSeEscribe`.
  await escribirEnvio(order.id, { agency_name: agencia?.name ?? null });
}

/**
 * Crea o actualiza el envío del pedido y agrega un evento al timeline.
 * El shipping_status del pedido lo DERIVA el trigger fn_order_shipments_derive.
 */
export async function saveShipment(
  order: OrderRecord,
  input: { carrier: string; tracking_number: string; status: string; note?: string; agency_ref?: string; secret_code?: string; agency_name?: string; package_size?: string; delivery_mode?: 'domicilio' | 'agencia' },
): Promise<void> {
  const event: ShipmentEvent = { status: input.status, at: new Date().toISOString(), note: input.note ?? null };
  /*
   * Por la puerta de la base (`elEnvioSeEscribe`): escribe el envío del paquete que corresponde —en Drop,
   * el almacén que registra desde el encargo escribe el de la venta— y agrega el evento allá, no acá.
   *
   * agency_ref/secret_code solo se tocan si se pasan (un avance de estado no los borra).
   * 🪤 delivery_mode, SOLO SI SE PASA, como el resto de los opcionales: un avance de estado no debe
   *    reescribir cómo se entrega un pedido. Se agregó para el reparto propio, que es entrega a domicilio
   *    aunque no lo lleve un courier — y sin esto quedaba en null, indistinguible de los 15.489 envíos
   *    viejos que nunca declararon su modo.
   */
  await escribirEnvio(order.id, {
    carrier: input.carrier || null,
    tracking_number: input.tracking_number || null,
    status: input.status,
    status_source: 'manual',
    agency_ref: input.agency_ref !== undefined ? input.agency_ref || null : undefined,
    delivery_mode: input.delivery_mode,
    secret_code: input.secret_code !== undefined ? input.secret_code || null : undefined,
    agency_name: input.agency_name !== undefined ? input.agency_name || null : undefined,
    package_size: input.package_size !== undefined ? input.package_size || null : undefined,
  }, { evento: event, crear: true });
}

/**
 * La agencia donde ESTE cliente retira siempre, sacada de su último envío real.
 *
 * ⭐ POR QUÉ EXISTE. El selector proponía la PRIMERA agencia del distrito por orden alfabético. Para
 * un cliente de Coronel Portillo eso es «Calleria Av Saenz Peña», cuando el que retira hace tres
 * envíos seguidos en «Pucallpa Co Federico Basadre». La ficha del chat ya mostraba la agencia
 * correcta en «Entrega habitual» — el dato estaba, el selector no lo miraba.
 *
 * 🚨 Y EL ERROR ES CARO Y SILENCIOSO: el paquete se despacha a una agencia a la que el cliente no va,
 * él reclama que no llegó, y alguien tiene que pagar el reenvío. Un desplegable con el valor
 * equivocado ya elegido es peor que uno vacío, porque nadie revisa lo que ya viene puesto.
 *
 * 🪤 Se busca por los últimos 9 dígitos del teléfono, el mismo criterio que usa todo el sistema para
 * reconocer a una persona.
 */
export async function fetchUltimaAgenciaDelCliente(tenantId: string, phone: string | null): Promise<string | null> {
  const tel = (phone ?? '').replace(/[^0-9]/g, '').slice(-9);
  if (!tel) return null;
  const { data } = await supabase
    .from('order_shipments')
    .select('agency_name, created_at, orders_v2!inner(customer_phone)')
    .eq('tenant_id', tenantId)
    .eq('carrier', 'Shalom')
    .not('agency_name', 'is', null)
    .ilike('orders_v2.customer_phone', `%${tel}`)
    .order('created_at', { ascending: false })
    .limit(1);
  const fila = data?.[0] as { agency_name?: string | null } | undefined;
  const nombre = (fila?.agency_name ?? '').trim();
  return nombre || null;
}

/** Registra el envío en Shalom en tiempo real (reusa el edge fn `shalom-register`, que
 *  es el adaptador oficial: registra contra la API de Shalom y guarda la guía). El módulo
 *  Operaciones pasa la agencia elegida como override. Devuelve guía + clave de agencia
 *  para reflejarlas en order_shipments. orders_v2.id === cod_orders.id (parallel run). */
export async function registerShipmentShalom(
  order: OrderRecord,
  agency: { id: string; name: string | null },
  opts?: {
    clave?: string; box?: { tipo_producto: number; name: string; detalle: string; costo: string }; aereo?: boolean; originAgencyId?: string | null;
    /** Venta de Drop: con la cuenta Shalom PRO del drop ('propia') o con la del almacén (lo de siempre). */
    cuenta?: 'propia' | 'almacen';
    /** Cobro Seguro de Shalom: el cliente paga este monto al recoger y Shalom se lo deposita al drop. */
    cobroSeguro?: number | null;
  },
): Promise<{ trackingNumber?: string; agencyKey?: string; secretCode?: string; message?: string; price?: number; aviso?: string }> {
  const { data, error } = await supabase.functions.invoke('shalom-register', {
    body: {
      cod_order_id: order.id,
      shalom_agency_id: agency.id,
      shalom_agency_name: agency.name,
      clave: opts?.clave,
      box: opts?.box,
      aereo: opts?.aereo,
      // Origen elegido en Entrega (override del default de config); el edge fn cae a config si no viene.
      origin_agency_id: opts?.originAgencyId ?? undefined,
      cuenta: opts?.cuenta,
      cobro_seguro: opts?.cobroSeguro ? { monto: opts.cobroSeguro } : undefined,
    },
  });
  const res = (data ?? {}) as { success?: boolean; error?: string; message?: string; trackingNumber?: string; agencyKey?: string; secretCode?: string; price?: number; aviso?: string; shalom_response_preview?: string };
  const withDetail = (m: string) => (res.shalom_response_preview ? `${m} · Shalom: ${res.shalom_response_preview}` : m);
  if (error) throw new Error(withDetail(res?.message || res?.error || await errorDeEdgeFunction(error, 'No se pudo registrar en Shalom.')));
  if (!res.success) throw new Error(withDetail(res?.message || res?.error || 'No se pudo registrar en Shalom.'));
  return { trackingNumber: res.trackingNumber, agencyKey: res.agencyKey, secretCode: res.secretCode, message: res.message, price: res.price, aviso: res.aviso };
}

/** Resultado del registro masivo en Shalom. */
export interface BulkShalomResult {
  ok: { id: string; trackingNumber?: string }[];
  skipped: { id: string; reason: string }[];
  failed: { id: string; error: string }[];
}

/** Un pedido del lote, con la agencia destino ya resuelta y el motivo si no se puede registrar. */
export interface ShalomBulkPlanItem {
  orderId: string;
  orderNumber: string;
  destino: string;
  agency: { id: string; name: string } | null;
  /** Precisión del match de ubigeo: 'district' es exacto; 'province'/'department' es aproximado. */
  matchedLevel: MatchedLevel;
  /** Si viene con texto, el pedido NO se puede registrar (ya tiene guía, sin ubigeo, sin agencia). */
  blocked: string | null;
}

/**
 * Arma el PLAN del registro masivo: por cada pedido resuelve la agencia destino a partir de su
 * ubigeo con `filterAgenciesByUbigeo` — el MISMO resolvedor que usa el picker de un pedido, para que
 * no existan dos lógicas de agencia que puedan divergir.
 *
 * Se resuelve en vez de guardarse: la agencia es derivable de la dirección, y una columna nueva
 * sería un contrato para siempre (poblarla, sincronizarla, migrarla). Ver ADR-011 §3.
 *
 * La idempotencia ("ya tiene guía") se consulta en `order_shipments`, la única verdad del envío.
 * Antes se leía `order_delivery_profile.shalom_agency_id`, que está vacía en las 15 603 filas: por eso el
 * registro masivo saltaba TODOS los pedidos con "Sin agencia Shalom asignada" y nunca registró nada.
 *
 * NO registra nada: devuelve el plan para previsualizarlo. Crear guías es irreversible y cuesta
 * plata, así que el operador confirma viendo a qué agencia va cada pedido.
 */
export async function planBulkRegisterShalom(
  tenantId: string,
  orderIds: string[],
): Promise<ShalomBulkPlanItem[]> {
  if (orderIds.length === 0) return [];

  const [ordersRes, profilesRes, shipmentsRes, agenciesRes] = await Promise.all([
    supabase.from('orders_v2').select('id, order_number, courier_tenant_id').in('id', orderIds),
    supabase.from('order_delivery_profile').select('order_id, department, province, district').in('order_id', orderIds),
    supabase.from('order_shipments').select('order_id, tracking_number, carrier, status').in('order_id', orderIds),
    // Misma consulta que el picker de un pedido (agencias activas habilitadas como destino).
    supabase.from('shalom_agencies').select('id, name, address, distrito, department, province')
      .eq('is_active', true).or('es_destino.is.null,es_destino.eq.true').order('name').limit(2000),
  ]);

  const numberById = new Map((ordersRes.data ?? []).map((o) => [o.id as string, (o as { order_number?: string }).order_number ?? '—']));
  const profileById = new Map((profilesRes.data ?? []).map((p) => [(p as { order_id: string }).order_id, p as Record<string, string | null>]));
  const { conGuia, conOtro } = lasQueYaVanConOtroTransportista((ordersRes.data ?? []) as { id: string; courier_tenant_id: string | null }[], (shipmentsRes.data ?? []) as EnvioDelLote[]);

  type AgencyLite = { id: string; name: string; address: string | null; distrito: string | null; department: string | null; province: string | null };
  const agencies = (agenciesRes.data ?? []) as AgencyLite[];
  const accessors = {
    getDept: (a: AgencyLite) => a.department,
    getProv: (a: AgencyLite) => a.province,
    getDist: (a: AgencyLite) => a.distrito,
    hasAddress: (a: AgencyLite) => a.address != null && String(a.address).trim().length > 0,
  };

  return orderIds.map((orderId) => {
    const orderNumber = numberById.get(orderId) ?? '—';
    const prof = profileById.get(orderId);
    const department = prof?.department ?? null;
    const province = prof?.province ?? null;
    const district = prof?.district ?? null;
    const destino = [district, province, department].filter(Boolean).map((x) => toTitle(String(x))).join(', ') || 'Sin dirección';

    const bloqueo = conGuia.has(orderId) ? 'Ya tiene guía' : conOtro.has(orderId) ? `Va con ${conOtro.get(orderId)}: se cambia en la venta` : null;
    if (bloqueo) return { orderId, orderNumber, destino, agency: null, matchedLevel: 'none' as MatchedLevel, blocked: bloqueo };
    if (!department) {
      return { orderId, orderNumber, destino, agency: null, matchedLevel: 'none' as MatchedLevel, blocked: 'Sin ubigeo en el pedido' };
    }

    const { matched, matchedLevel } = filterAgenciesByUbigeo(agencies, { department, province, district }, accessors);
    const best = matched[0];
    if (!best) {
      return { orderId, orderNumber, destino, agency: null, matchedLevel, blocked: 'Sin agencia Shalom en esa zona' };
    }
    return { orderId, orderNumber, destino, agency: { id: best.id, name: best.name }, matchedLevel, blocked: null };
  });
}

/**
 * Registro MASIVO en Shalom: ejecuta el plan que el operador YA confirmó (ver
 * `planBulkRegisterShalom`, que resuelve la agencia de cada pedido y se previsualiza antes). Usa el
 * MISMO adaptador que el registro individual (edge fn shalom-register) + los defaults del tenant
 * (origen/caja). Genera una clave de recojo aleatoria por pedido, crea el envío (order_shipments) y
 * persiste el costo + pagador del flete. Es SECUENCIAL (la sesión firmada de Shalom no tolera
 * ráfagas) e IDEMPOTENTE: respeta los `blocked` del plan (p. ej. "Ya tiene guía"). Modo terrestre
 * (el aéreo exige validar la ruta agencia por agencia → se hace en Entrega individual).
 */
export async function bulkRegisterShalom(
  tenantId: string,
  items: ShalomBulkPlanItem[],
  onProgress?: (done: number, total: number) => void,
): Promise<BulkShalomResult> {
  const result: BulkShalomResult = { ok: [], skipped: [], failed: [] };

  // Pagador del flete por defecto del tenant (para que el margen quede bien). Best-effort.
  let freightPayer: 'origin' | 'destination' = 'origin';
  try {
    if ((await configGeneralDelNegocio(tenantId)).shalom_default_freight_payer === 'destination') freightPayer = 'destination';
  } catch { /* default origen */ }

  let done = 0;
  for (const item of items) {
    const id = item.orderId;
    const orderLite = { id, tenant_id: tenantId } as OrderRecord;
    try {
      // El plan ya trae la agencia resuelta y validada; acá sólo se respeta lo que el operador confirmó.
      if (item.blocked) { result.skipped.push({ id, reason: item.blocked }); continue; }
      const agencyId = item.agency?.id ?? null;
      const agencyName = item.agency?.name ?? null;
      if (!agencyId) { result.skipped.push({ id, reason: 'Sin agencia Shalom en esa zona' }); continue; }

      const clave = String(Math.floor(1000 + Math.random() * 9000));
      const { trackingNumber, agencyKey, secretCode, price } = await registerShipmentShalom(
        orderLite, { id: agencyId, name: agencyName }, { clave },
      );
      await saveShipment(orderLite, {
        carrier: 'Shalom', tracking_number: trackingNumber ?? '', agency_ref: agencyKey ?? '',
        agency_name: agencyName ?? undefined, secret_code: secretCode ?? clave, status: 'registrado',
        note: ['Registrado en Shalom (masivo)', trackingNumber ? `Guía ${trackingNumber}` : null].filter(Boolean).join(' · '),
      });
      await updateFreight(orderLite, { ...(Number(price) > 0 ? { shipping_cost: Number(price) } : {}), freight_payer: freightPayer });
      result.ok.push({ id, trackingNumber });
    } catch (err) {
      result.failed.push({ id, error: err instanceof Error ? err.message : 'Error' });
    } finally {
      done += 1;
      onProgress?.(done, items.length);
    }
  }
  return result;
}

/** Tarifa real de Shalom por modo para la ruta origen(tenant)→destino. `aereo` es null si esa ruta
 *  no admite aéreo (así sabemos, por ruta y no por agencia, si hay aéreo y a qué precio). */
export interface ShalomRouteTariff {
  terrestre: Record<string, unknown> | null;
  aereo: Record<string, unknown> | null;
}
/**
 * 🚨 ANTES ESTA FUNCIÓN SE TRAGABA EL MOTIVO EN TRES LUGARES: `if (error) return null`,
 * `if (!res?.ok) return null` y un `catch` vacío. Los tres devolvían lo mismo —null— así que
 * «Shalom no está conectado», «falta la agencia de origen» y «no hay cobertura en esta ruta» se
 * veían EXACTAMENTE IGUAL en la pantalla: un precio por defecto, sin aviso.
 *
 * Por eso el founder vio la misma tarifa para terrestre y aéreo y precios que no coinciden con el
 * portal: sin tarifa, los dos modos caen al mismo costo por defecto de la caja. El síntoma era la
 * consecuencia; la causa —que Shalom no respondió y por qué— no se mostraba en ningún lado.
 *
 * Ahora el motivo viaja. Que se muestre o no es decisión de la pantalla, pero perderlo acá dejaba a
 * todos —al operador y a quien depura— sin nada que mirar.
 */
export interface RespuestaDeTarifa {
  tarifa: ShalomRouteTariff | null;
  /**
   * `tipo_producto` que ESTA ruta acepta, por modo. `null` = sin restricción conocida.
   *
   * ⭐ No es una propiedad de la agencia: es la intersección de lo que el origen puede ENVIAR y lo
   * que el destino puede RECIBIR. Y el aéreo tiene además una regla propia del portal — la caja L
   * no viaja por avión.
   *
   * 🪤 `null` significa «no sé», y se trata como «se permite todo»: ocultar una opción válida por un
   * fallo de red sería peor que dejar que Shalom rechace. Una lista VACÍA sí sería «nada permitido»,
   * por eso se distinguen.
   */
  permitidos?: { terrestre: number[] | null; aereo: number[] | null };
  /** Por qué no hay tarifa, en palabras del servidor. Null si sí la hay. */
  motivo: string | null;
}

export async function fetchShalomTariff(
  tenantId: string,
  destAgencyId: string,
  originAgencyId?: string | null,
  /**
   * ⭐ LA VENTA, PARA QUE COTICE QUIEN VA A DESPACHAR. El servidor resuelve con ella la cuenta —no se
   *    le manda el tenant del almacén desde acá, que sería dejar que cualquiera cotice como
   *    cualquiera—. Sin esto, un revendedor sin Shalom Pro no ve cajas ni aéreo/terrestre aunque el
   *    despacho lo haga el almacén.
   */
  ventaId?: string | null,
  /** Venta de Drop registrada con la cuenta Shalom PRO del drop: se cotiza con ESA cuenta (20-oct-2026). */
  cuenta?: 'propia' | 'almacen' | null,
): Promise<RespuestaDeTarifa> {
  try {
    const { data, error } = await supabase.functions.invoke('shalom-tariff', {
      body: {
        tenant_id: tenantId,
        dest_agency_id: destAgencyId,
        origin_agency_id: originAgencyId ?? undefined,
        order_id: ventaId ?? undefined,
        cuenta: cuenta === 'propia' ? 'propia' : undefined,
      },
    });
    if (error) return { tarifa: null, motivo: await errorDeEdgeFunction(error, 'Shalom no respondió la tarifa.') };
    const res = data as { ok?: boolean; error?: string; terrestre?: Record<string, unknown> | null; aereo?: Record<string, unknown> | null; permitidos_terrestre?: number[] | null; permitidos_aereo?: number[] | null };
    if (!res?.ok) return { tarifa: null, motivo: res?.error ?? 'Shalom no devolvió la tarifa.' };
    const r = res as typeof res & { permitidos_terrestre?: number[] | null; permitidos_aereo?: number[] | null };
    return {
      tarifa: { terrestre: res.terrestre ?? null, aereo: res.aereo ?? null },
      motivo: null,
      permitidos: { terrestre: r.permitidos_terrestre ?? null, aereo: r.permitidos_aereo ?? null },
    };
  } catch (e) {
    return { tarifa: null, motivo: e instanceof Error ? e.message : 'No se pudo consultar la tarifa.' };
  }
}

/**
 * Guarda el caché del último rastreo en order_shipments (patrón "memoria primero": el modal de
 * estado lo lee para render instantáneo). Lo hace el frontend con la sesión autenticada porque
 * la RLS de order_shipments concede escritura al rol `authenticated` por tenant. Best-effort:
 * no debe romper la consulta si falla. Si `codigoOrden` viene y el envío no tenía agency_ref,
 * lo auto-repara de paso (para que el próximo rastreo no tenga que re-resolverlo).
 */
export async function saveShipmentTracking(
  _tenantId: string,
  shipmentId: string,
  tracking: unknown,
  codigoOrden?: string | null,
): Promise<void> {
  // Por la puerta (`elEnvioSeEscribe`): el almacén también guarda el rastreo del paquete de la venta.
  await escribirEnvio(null, {
    last_tracking: tracking,
    last_tracked_at: new Date().toISOString(),
    agency_ref: codigoOrden || undefined,
  }, { envioId: shipmentId });
}

/** Consulta el estado del envío en Shalom (reusa el edge fn `shalom-track`). Devuelve el
 *  payload crudo de Shalom para renderizar con ShalomTrackingCard (estado, imágenes, ticket). */
export async function trackShipmentShalom(
  order: OrderRecord,
  // `docNumber`: llave alternativa cuando el envío se registró fuera de Smarty y nadie anotó la
  // guía. Shalom trae el documento del destinatario en cada fila de su historial.
  input: { trackingNumber?: string | null; agencyKey?: string | null; docNumber?: string | null },
): Promise<unknown> {
  const { data, error } = await supabase.functions.invoke('shalom-track', {
    body: {
      cod_order_id: order.id,
      tracking_number: input.trackingNumber ?? '',
      agency_key: input.agencyKey ?? '',
      doc_number: input.docNumber ?? '',
    },
  });
  const json = data as { success?: boolean; data?: unknown; error?: string } | null;
  if (error) throw new Error(json?.error || await errorDeEdgeFunction(error, 'No se pudo consultar el estado en Shalom.'));
  if (!json?.success || json.data == null) throw new Error(json?.error ?? 'Shalom no devolvió estado para esta guía.');
  return json.data;
}

/** Un courier disponible en Aliclik para un destino (de la cotización shipping/cost). */
export interface AliclikCourierOption {
  transportId: number;
  transportName: string;
  deliveryCost: number;
  /** Sobrecosto de express (se suma a deliveryCost si se despacha express). 0 si no aplica. */
  additionalCostExpress: number;
  returnCost: number;
  addDays: number;
  /** true = el courier SOPORTA express (no que sea express). */
  flagDeliveryExpress: boolean;
  /** Hora de corte (HH:mm) del almacén para despacho normal. */
  schedule: string | null;
  scheduleExpressStart: string | null;
  scheduleExpressEnd: string | null;
  transportUrlImage: string | null;
}

/**
 * Cotiza en Aliclik los couriers disponibles para el pedido (SIN registrar), para que el operador
 * elija cuál usar. Reutiliza `aliclik-register` en modo `quote_only`: misma resolución/validación
 * (homologación EAN + almacén único + lat/lng), pero devuelve la lista de couriers en vez de crear.
 */
export async function quoteAliclikCouriers(
  order: OrderRecord,
): Promise<{ couriers: AliclikCourierOption[]; resolvedDistrict: string | null }> {
  const { data, error } = await supabase.functions.invoke('aliclik-register', {
    body: { tenant_id: order.tenant_id, order_id: order.id, quote_only: true },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string; detail?: string; unmapped?: string[]; couriers?: AliclikCourierOption[]; resolvedDistrict?: string | null };
  const extra = res.detail ? ` · ${res.detail}` : res.unmapped?.length ? ` (${res.unmapped.join(', ')})` : '';
  // supabase-js oculta el body del 4xx tras "Edge Function returned a non-2xx…"; parseEdgeFunctionError
  // lee el error real que devolvió la función (p. ej. "productos sin homologar").
  if (error) throw new Error(await parseEdgeFunctionError(error, res));
  if (res.error) throw new Error(res.error + extra);
  return { couriers: res.couriers ?? [], resolvedDistrict: res.resolvedDistrict ?? null };
}

/**
 * Registra el pedido en Aliclik (edge fn `aliclik-register`, API oficial — dropshipping por EAN).
 * El edge fn cotiza el envío, elige courier y crea el pedido; persiste el orderNumber en
 * order_shipments. Requiere productos homologados a EAN + dirección geocodificada (lat/lng).
 */
export interface AliclikAgencyRegistration {
  id: string; name: string; address: string;
  packageSize: string; keyCode: string; scheduleDate: string; reference?: string;
  firstName: string; firstLastName: string; secondLastName: string;
  docType?: string; docNumber: string;
}

export async function registerShipmentAliclik(
  order: OrderRecord,
  opts?: { transportId?: number; express?: boolean; agency?: AliclikAgencyRegistration },
): Promise<{ orderNumber?: string; already?: boolean }> {
  const { data, error } = await supabase.functions.invoke('aliclik-register', {
    body: {
      tenant_id: order.tenant_id, order_id: order.id,
      // El modo viaja explícito: el backend elige con él el endpoint (son dos flujos distintos).
      mode: opts?.agency ? 'agencia' : 'domicilio',
      transport_id: opts?.transportId, express: opts?.express, agency: opts?.agency,
    },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string; detail?: string; unmapped?: string[]; order_number?: string; already?: boolean };
  const extra = res.detail ? ` · ${res.detail}` : res.unmapped?.length ? ` (${res.unmapped.join(', ')})` : '';
  if (error) throw new Error(await parseEdgeFunctionError(error, res));
  if (!res.ok) throw new Error((res?.error || 'No se pudo registrar en Aliclik.') + extra);
  return { orderNumber: res.order_number, already: res.already };
}

/**
 * Despacha un pedido de Mercado Libre (PREPAGO) por Aliclik SIN cobro contra entrega (edge fn
 * `mercadolibre-fulfill`, modo "cobrar 0"). ML ya cobró; Aliclik solo despacha. Requiere productos
 * homologados a EAN + coordenadas del envío de Mercado Envíos. Persiste el orderNumber en
 * order_shipments (a partir de ahí la orden deja de contar como comprometida en el stock-sync).
 */
export async function fulfillMercadoLibreOrder(
  order: OrderRecord,
  opts?: { transportId?: number },
): Promise<{ orderNumber?: string; already?: boolean }> {
  const { data, error } = await supabase.functions.invoke('mercadolibre-fulfill', {
    body: { tenant_id: order.tenant_id, order_id: order.id, transport_id: opts?.transportId },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string; detail?: string; unmapped?: string[]; order_number?: string; already?: boolean };
  const extra = res.detail ? ` · ${res.detail}` : res.unmapped?.length ? ` (${res.unmapped.join(', ')})` : '';
  if (error) throw new Error((res?.error || await errorDeEdgeFunction(error, 'No se pudo despachar por Aliclik.')) + extra);
  if (!res.ok) throw new Error((res?.error || 'No se pudo despachar por Aliclik.') + extra);
  return { orderNumber: res.order_number, already: res.already };
}

/** Consulta el estado del pedido en Aliclik (reusa el edge fn `aliclik-track`). Devuelve el estado
 *  normalizado + los 3 ejes de Aliclik (callStatus / status / dispatchStatus) + el item crudo. */
export async function trackShipmentAliclik(
  order: OrderRecord,
  input: { orderNumber?: string | null },
): Promise<{ status: string; callStatus?: string; aliclikStatus?: string; dispatchStatus?: string; serviceName?: string | null; serviceMode?: string | null; serviceFromAliclik?: boolean; appliedStatus?: string | null; agencyNotQueryable?: boolean; raw: Record<string, unknown> }> {
  const { data, error } = await supabase.functions.invoke('aliclik-track', {
    body: { tenant_id: order.tenant_id, order_id: order.id, order_number: input.orderNumber ?? '' },
  });
  const res = data as { ok?: boolean; status?: string; call_status?: string; aliclik_status?: string; dispatch_status?: string; service_name?: string | null; service_mode?: string | null; service_from_aliclik?: boolean; applied_status?: string | null; agency_not_queryable?: boolean; raw?: Record<string, unknown>; error?: string } | null;
  // supabase-js tapa el cuerpo del 4xx tras "Edge Function returned a non-2xx…": sin esto, un
  // "Aliclik no devolvió ese pedido" —el caso normal de los envíos creados con otro token— llegaba
  // a la pantalla como un error de infraestructura que no le dice nada a nadie.
  if (error) throw new Error(await parseEdgeFunctionError(error, res ?? {}));
  if (!res?.ok) throw new Error(res?.error ?? 'Aliclik no devolvió estado para este pedido.');
  return {
    status: res.status ?? '', callStatus: res.call_status, aliclikStatus: res.aliclik_status, dispatchStatus: res.dispatch_status,
    serviceName: res.service_name ?? null, serviceMode: res.service_mode ?? null, serviceFromAliclik: res.service_from_aliclik === true,
    // Estado que aliclik-track dejó guardado en el envío (null = no tocó nada).
    appliedStatus: res.applied_status ?? null,
    // true = es un envío por agencia y Aliclik no lo expone en su API de consulta (no es un error).
    agencyNotQueryable: res.agency_not_queryable === true,
    raw: res.raw ?? {},
  };
}

/**
 * Cancela en Aliclik un pedido registrado. Aliclik solo cancela con el pedido confirmado,
 * "Por entregar" y sin despachar del todo; si aún no está confirmado deja una NOTA y no
 * cancela — ese caso vuelve como `pending: true` (el envío sigue vivo).
 */
export async function cancelShipmentAliclik(
  order: OrderRecord,
): Promise<{ pending: boolean; message: string }> {
  const { data, error } = await supabase.functions.invoke('aliclik-cancel', {
    body: { tenant_id: order.tenant_id, order_id: order.id },
  });
  const res = (data ?? {}) as { ok?: boolean; pending?: boolean; message?: string; error?: string; detail?: string };
  const extra = res.detail ? ` · ${res.detail}` : '';
  if (error) throw new Error((res?.error || await errorDeEdgeFunction(error, 'No se pudo cancelar en Aliclik.')) + extra);
  if (!res.ok) throw new Error((res?.error || 'No se pudo cancelar en Aliclik.') + extra);
  return { pending: res.pending === true, message: res.message ?? 'Pedido cancelado en Aliclik.' };
}

/**
 * Etiquetas en español de los 3 ejes de estado de Aliclik (doc oficial). La API responde
 * enums crudos (CONFIRMED, TO_PREPARE…): esto es lo único que los traduce para la UI.
 */
export const ALICLIK_CALL_STATUS_ES: Record<string, string> = {
  CONFIRMED: 'Confirmado',
  FOLLOW: 'En seguimiento',
  CALL_LATER: 'Llamar más tarde',
  ANNULLED: 'Anulado',
  NOT_RESPOND: 'No contesta',
  DUPLICATE: 'Duplicado',
  OUT_OF_STOCK: 'Sin stock',
  NO_COVERAGE: 'Sin cobertura',
  FAKE: 'Falso',
  TESTING: 'Prueba',
  CONTACTED: 'Contactado',
  IMPORTED: 'Importado',
};
export const ALICLIK_STATUS_ES: Record<string, string> = {
  PENDING_DELIVERY: 'Por entregar',
  DELIVERED: 'Entregado',
  RESCHEDULED: 'Reprogramado',
  REFUSED: 'Rechazado',
  NOT_RESPOND: 'No contesta',
  CANCEL: 'Cancelado',
  ANNULLED: 'Anulado',
};
export const ALICLIK_DISPATCH_STATUS_ES: Record<string, string> = {
  TO_PREPARE: 'Por preparar',
  PREPARED: 'Preparado',
  PICKED: 'Validado',
  TO_RETURN: 'Por retornar',
  RETURNED: 'Retornado',
  IN_TRANSIT: 'En tránsito',
  IN_AGENCY: 'En agencia',
  LEFT_IN_WAREHOUSE: 'Dejado en almacén',
  STORE_CENTRAL: 'En tienda central',
  REMAINING_IN_TRANSIT: 'Pendiente en tránsito',
};

/** Traduce un enum de Aliclik a español; si es desconocido devuelve el crudo (no oculta datos). */
export function aliclikLabel(kind: 'call' | 'status' | 'dispatch', value?: string | null): string {
  const v = (value ?? '').toUpperCase().trim();
  if (!v) return '—';
  const dict = kind === 'call' ? ALICLIK_CALL_STATUS_ES : kind === 'status' ? ALICLIK_STATUS_ES : ALICLIK_DISPATCH_STATUS_ES;
  return dict[v] ?? v;
}

// ---- Aliclik: catálogo + homologación producto→EAN (modelo dropshipping) ----

export interface AliclikCatalogSku {
  /** 🪤 Aliclik NO devuelve `sku` en el catálogo (verificado 15-ago-2026: sus SKUs traen ean,
   *  regularPrice, name, dropPrice, stockVirtual, warehouseId, warehouseName y nada más). El tipo
   *  decía que venía siempre y se guardaba `undefined` en aliclik_product_map.sku. El identificador
   *  real del modelo es el EAN. */
  sku?: string; ean: string; name: string; regularPrice: number; dropPrice: number; stockVirtual: number; warehouseId: number; warehouseName: string;
}
export interface AliclikCatalogProduct {
  id: number; name: string; urlImage?: string; category?: string; skus: AliclikCatalogSku[];
}

/** Una agencia de Shalom donde Aliclik puede dejar el paquete (directorio del proveedor). */
export interface AliclikAgencyOption {
  id: number | string; name: string;
  address?: string | null; department?: string | null; province?: string | null; district?: string | null;
}

/**
 * Directorio para despachar A AGENCIA: las agencias de Shalom (546 al 15-ago-2026) y los tipos de
 * paquete admitidos. Los dos vienen juntos porque la pantalla los necesita a la vez.
 *
 * 🪤 Los tamaños salen de ACÁ, no de `SHALOM_BOXES`: son parecidos pero la lista que Aliclik acepta
 * en `merchandiseShalom` es la suya (incluye "SOBRE", que Smarty no tiene).
 */
export async function fetchAliclikAgencies(
  tenantId: string,
): Promise<{ agencies: AliclikAgencyOption[]; packageSizes: string[] }> {
  const { data, error } = await supabase.functions.invoke('aliclik-agencies', { body: { tenant_id: tenantId } });
  const res = data as { ok?: boolean; agencies?: AliclikAgencyOption[]; package_sizes?: string[]; error?: string } | null;
  if (error) throw new Error(await parseEdgeFunctionError(error, res ?? {}));
  if (!res?.ok) throw new Error(res?.error ?? 'No se pudieron traer las agencias de Aliclik.');
  return { agencies: res.agencies ?? [], packageSizes: res.package_sizes ?? [] };
}

/** Busca en el catálogo de Aliclik (vía edge fn `aliclik-catalog`, token server-side). */
export async function fetchAliclikCatalog(tenantId: string, search?: string): Promise<AliclikCatalogProduct[]> {
  // `all: true` = el catálogo COMPLETO, paginado del lado del servidor. Sin esto llegaban 20
  // productos (~44 tarjetas) de 2.336: la pantalla filtra y ordena sobre el conjunto, y con media
  // lista un filtro miente.
  const { data, error } = await supabase.functions.invoke('aliclik-catalog', { body: { tenant_id: tenantId, search: search?.trim() || undefined, all: true } });
  const res = data as { ok?: boolean; result?: AliclikCatalogProduct[]; error?: string } | null;
  if (error) throw new Error(await parseEdgeFunctionError(error, res ?? {}));
  if (!res?.ok) throw new Error(res?.error ?? 'No se pudo consultar el catálogo de Aliclik.');
  return res.result ?? [];
}

export interface AliclikProductMapRow {
  id: string; product_id: string; ean: string; sku: string | null; warehouse_id: number | null; aliclik_name: string | null;
}
/** Homologaciones producto Smarty → Aliclik del tenant. */
export async function listAliclikProductMap(tenantId: string): Promise<AliclikProductMapRow[]> {
  const { data, error } = await supabase
    .from('aliclik_product_map')
    .select('id, product_id, ean, sku, warehouse_id, aliclik_name')
    .eq('tenant_id', tenantId);
  if (error) throw error;
  return (data ?? []) as AliclikProductMapRow[];
}
/** Guarda/actualiza la homologación de un producto (upsert por tenant+product). */
export async function upsertAliclikProductMap(
  tenantId: string,
  entry: { product_id: string; ean: string; sku?: string | null; warehouse_id?: number | null; aliclik_name?: string | null },
): Promise<void> {
  const { error } = await supabase
    .from('aliclik_product_map')
    .upsert({ tenant_id: tenantId, ...entry, updated_at: new Date().toISOString() }, { onConflict: 'tenant_id,product_id' });
  if (error) throw error;
}
/** Quita la homologación de un producto. */
export async function deleteAliclikProductMap(id: string): Promise<void> {
  const { error } = await supabase.from('aliclik_product_map').delete().eq('id', id);
  if (error) throw error;
}

/** Un SKU del catálogo de Aliclik listo para importar (dropshipping). */
export interface AliclikPickedItem {
  ean: string;
  sku?: string | null;
  aliclik_product_id?: number | null;
  name: string;
  image_url?: string | null;
  drop_price?: number | null;      // costo dropshipper
  regular_price?: number | null;   // precio de venta sugerido
  stock_virtual?: number | null;
  warehouse_id?: number | null;
}


/**
 * Nuestro origen de medios. Todo lo que no empiece así es de un tercero.
 * Debe coincidir con `R2_PUBLIC_URL` de las edge functions.
 */
const MEDIA_BASE = 'https://media.smarty.lat';

/**
 * Cuántos productos siguen enlazando la foto a un servidor ajeno.
 *
 * Existe para que el comercio SE ENTERE. La ingesta arregla lo que se importa
 * de ahora en más, pero lo ya importado se queda como está hasta que alguien lo
 * traiga, y un problema que solo se ve cuando el proveedor borra la foto no se
 * ve nunca a tiempo.
 */
export async function countForeignProductImages(tenantId: string): Promise<number> {
  // 🪤 Se cuenta en código y no con `image_url NOT LIKE '<r2>%'`, que era la
  // primera versión: esa consulta NUNCA veía a un producto con la portada propia
  // y la GALERÍA enlazada afuera — el caso de quien reemplazó la principal a
  // mano y se olvidó del resto. "¿Algún elemento del arreglo empieza con...?" no
  // se expresa bien en PostgREST.
  const { data, error } = await supabase
    .from('products')
    .select('image_url, image_urls')
    .eq('tenant_id', tenantId)
    .or('image_url.not.is.null,image_urls.not.is.null')
    .limit(1000);                             // 🪤 PostgREST corta ahí igual
  if (error || !data) return 0;               // informativo: no rompe la página
  const ajena = (u: unknown): boolean =>
    typeof u === 'string' && /^https?:\/\//i.test(u) && !u.startsWith(MEDIA_BASE);
  return data.filter((p) => {
    const g = (p as { image_urls?: string[] | null }).image_urls;
    return ajena((p as { image_url?: string | null }).image_url) || (Array.isArray(g) && g.some(ajena));
  }).length;
}

export interface BackfillImagenesResult {
  /** Productos con al menos una foto ajena. */
  revisados: number;
  /** Fotos puestas en cola. El worker las trae; no hay que volver a apretar. */
  encoladas: number;
  /** `true` si había más productos de los que entran en una consulta. */
  hayMas: boolean;
}

/**
 * Trae a R2 las fotos de los productos que quedaron enlazando a un tercero.
 *
 * Va por la MISMA edge function que la importación (modo `backfill`), no por un
 * camino propio: si la ingesta viviera dos veces, las dos copias se separarían
 * y la que corre menos seguido sería la que está mal.
 *
 * No despublica nada: esos productos ya están vivos y pueden estar vendiéndose.
 */
export async function backfillSupplierImages(
  tenantId: string,
  opts?: { dryRun?: boolean },
): Promise<BackfillImagenesResult> {
  const { data, error } = await supabase.functions.invoke('import-supplier-product', {
    body: { tenant_id: tenantId, mode: 'backfill', dry_run: opts?.dryRun },
  });
  const res = (data ?? {}) as {
    ok?: boolean; error?: string; detail?: string;
    revisados?: number; encoladas?: number; hayMas?: boolean;
  };
  const extra = res.detail ? ` · ${res.detail}` : '';
  if (error) throw new Error((res.error || await errorDeEdgeFunction(error, 'No se pudieron traer las fotos.')) + extra);
  if (!res.ok) throw new Error((res.error || 'No se pudieron traer las fotos.') + extra);
  return {
    revisados: res.revisados ?? 0,
    encoladas: res.encoladas ?? 0,
    hayMas: res.hayMas === true,
  };
}

/**
 * Importa un SKU del catálogo de Aliclik como PRODUCTO de Smarty (modelo dropshipping),
 * idempotente por EAN: si ya se importó ese EAN, reutiliza el producto existente. Crea el
 * producto (con costo = drop_price, precio = regular_price) + el binding aliclik_product_map,
 * y copia la foto del proveedor a R2.
 */
export async function createProductFromAliclik(tenantId: string, item: AliclikPickedItem): Promise<string> {
  const { productId } = await importFromSupplier(tenantId, 'aliclik', item);
  return productId;
}

/** Igual que `createProductFromAliclik`, pero devolviendo también qué pasó con la foto. */
export function importProductFromAliclik(
  tenantId: string,
  item: AliclikPickedItem,
): Promise<SupplierImportResult> {
  return importFromSupplier(tenantId, 'aliclik', item);
}

// ---- Dropi: catálogo + import + registro (dropshipping COD multi-país) ----
// Ver docs/adr/0004-integracion-dropi.md. El token vive cifrado server-side: todo pasa por
// edge functions. A diferencia de Aliclik, el token es autoservicio del panel de Dropi.

/** Etiquetas de país de Dropi (código ISO → nombre) para los selectores. Fuente única. */
export const DROPI_COUNTRY_LABELS: Record<string, string> = {
  CO: 'Colombia', PE: 'Perú', MX: 'México', EC: 'Ecuador', CL: 'Chile',
  PA: 'Panamá', PY: 'Paraguay', AR: 'Argentina', CR: 'Costa Rica', ES: 'España',
};

export interface DropiCatalogVariation {
  id: number;
  stock: number;
  suggested_price: number;
  attributes: unknown;
}
export interface DropiCatalogProduct {
  id: number;
  name: string;
  sku: string | null;
  description: string | null;
  image: string | null;
  suggested_price: number;
  cost_price: number;
  stock: number;
  supplier_id: number | null;
  supplier_name: string | null;
  category: string | null;
  variations: DropiCatalogVariation[];
}

/** Busca en el catálogo de Dropi (edge fn `dropi-catalog`, token server-side). */
export async function fetchDropiCatalog(
  tenantId: string,
  opts?: { keywords?: string; page?: number; pageSize?: number },
): Promise<DropiCatalogProduct[]> {
  const { data, error } = await supabase.functions.invoke('dropi-catalog', {
    body: { tenant_id: tenantId, keywords: opts?.keywords?.trim() || undefined, page: opts?.page ?? 1, page_size: opts?.pageSize },
  });
  const res = data as { ok?: boolean; products?: DropiCatalogProduct[]; error?: string; detail?: string } | null;
  const extra = res?.detail ? ` · ${res.detail}` : '';
  if (error) throw new Error((res?.error || await errorDeEdgeFunction(error, 'No se pudo consultar el catálogo de Dropi.')) + extra);
  if (!res?.ok) throw new Error((res?.error ?? 'No se pudo consultar el catálogo de Dropi.') + extra);
  return res.products ?? [];
}

/** Stock EN VIVO de un producto de Dropi (el caché del mapeo no es autoritativo). */
export async function fetchDropiProductLive(tenantId: string, dropiProductId: number): Promise<DropiCatalogProduct> {
  const { data, error } = await supabase.functions.invoke('dropi-catalog', {
    body: { tenant_id: tenantId, product_id: dropiProductId },
  });
  const res = data as { ok?: boolean; product?: DropiCatalogProduct; error?: string } | null;
  if (error) throw new Error(res?.error || await errorDeEdgeFunction(error, 'No se pudo consultar el producto en Dropi.'));
  if (!res?.ok || !res.product) throw new Error(res?.error ?? 'Dropi no devolvió el producto.');
  return res.product;
}

/**
 * Claves de tarjeta ya importadas por el tenant, para marcar "Importado ✓" de forma PERSISTENTE
 * (sobrevive recargas) — igual que el vínculo producto Smarty ↔ marketplace. Devuelve claves con el
 * mismo formato que usa el catálogo: `dropi-{id}` / `aliclik-{ean}`.
 */
export async function fetchImportedCatalogKeys(
  tenantId: string,
  provider: 'dropi' | 'aliclik' | 'smartydrop',
): Promise<string[]> {
  if (provider === 'smartydrop') {
    const { data, error } = await supabase
      .from('drop_producto_importado')
      .select('catalogo_id, products!inner(is_active)').eq('products.is_active', true) // lo quitado de «Míos» se puede volver a traer
      .eq('tenant_id', tenantId);
    if (error) throw error;
    return (data ?? []).map((r) => `smartydrop-${(r as { catalogo_id: string }).catalogo_id}`);
  }
  if (provider === 'dropi') {
    const { data, error } = await supabase
      .from('dropi_product_map')
      .select('dropi_product_id')
      .eq('tenant_id', tenantId);
    if (error) throw error;
    return (data ?? []).map((r) => `dropi-${(r as { dropi_product_id: number }).dropi_product_id}`);
  }
  const { data, error } = await supabase
    .from('aliclik_product_map')
    .select('ean')
    .eq('tenant_id', tenantId);
  if (error) throw error;
  return (data ?? []).map((r) => `aliclik-${(r as { ean: string }).ean}`);
}

/** Fila del espejo global del marketplace de Dropi (tabla `dropi_marketplace`). */
export interface DropiMarketplaceRow {
  dropi_product_id: number;
  name: string;
  sku: string | null;
  description: string | null;
  image: string | null;
  suggested_price: number;
  cost_price: number;
  stock: number;
  category: string | null;
  supplier_name: string | null;
  variations: DropiCatalogVariation[];
}

/**
 * Lee el catálogo de Dropi desde NUESTRA BD (espejo). Búsqueda, categoría, orden y paginación son
 * server-side sobre nuestra base → instantáneo y sin golpear a Dropi (modelo DSers/Spocket).
 * Devuelve el total para paginación real (página N de M).
 */
/** Rangos de stock ("cantidad") para el filtro, alineados con los de Dropi. */
export type DropiStockRange = 'all' | 'lt100' | '100_500' | '500_1000' | 'gt1000';

export async function fetchDropiMarketplace(opts: {
  q?: string; category?: string; supplier?: string; stock?: DropiStockRange;
  minCost?: number | null; maxCost?: number | null;
  sort?: string; page: number; pageSize: number;
}): Promise<{ products: DropiMarketplaceRow[]; total: number }> {
  const from = (opts.page - 1) * opts.pageSize;
  const to = from + opts.pageSize - 1;
  let query = supabase
    .from('dropi_marketplace')
    .select('dropi_product_id,name,sku,description,image,suggested_price,cost_price,stock,category,supplier_name,variations', { count: 'exact' })
    .eq('is_active', true);
  const q = (opts.q ?? '').trim();
  if (q) query = query.ilike('name', `%${q}%`);
  if (opts.category && opts.category !== 'all') query = query.eq('category', opts.category);
  if (opts.supplier && opts.supplier !== 'all') query = query.eq('supplier_name', opts.supplier);
  // Rango de cantidad (stock).
  switch (opts.stock) {
    case 'lt100': query = query.lt('stock', 100); break;
    case '100_500': query = query.gte('stock', 100).lte('stock', 500); break;
    case '500_1000': query = query.gte('stock', 500).lte('stock', 1000); break;
    case 'gt1000': query = query.gt('stock', 1000); break;
    default: break;
  }
  // Rango de precio de proveedor (costo).
  if (opts.minCost != null) query = query.gte('cost_price', opts.minCost);
  if (opts.maxCost != null) query = query.lte('cost_price', opts.maxCost);
  // Orden + desempate estable por id (evita que grupos con el mismo valor se vean "desordenados").
  switch (opts.sort) {
    case 'margin': query = query.order('margin', { ascending: false }).order('dropi_product_id', { ascending: false }); break;
    case 'price_asc': query = query.order('suggested_price', { ascending: true }).order('dropi_product_id', { ascending: false }); break;
    case 'price_desc': query = query.order('suggested_price', { ascending: false }).order('dropi_product_id', { ascending: false }); break;
    case 'name': query = query.order('name', { ascending: true }); break;
    default: query = query.order('stock', { ascending: false }).order('dropi_product_id', { ascending: false }); break; // relevancia = con stock primero
  }
  const { data, error, count } = await query.range(from, to);
  if (error) throw error;
  return { products: (data ?? []) as unknown as DropiMarketplaceRow[], total: count ?? 0 };
}

/** Categorías distintas del espejo (con conteo) para el filtro. Vía RPC (evita el tope de 1000 filas). */
export async function fetchDropiMarketplaceCategories(): Promise<{ name: string; n: number }[]> {
  const { data, error } = await supabase.rpc('fn_dropi_marketplace_categories');
  if (error) throw error;
  return ((data ?? []) as { category: string; n: number }[]).map((r) => ({ name: r.category, n: Number(r.n) || 0 }));
}

/** Proveedores distintos del espejo (con conteo) para el filtro. Vía RPC. */
export async function fetchDropiMarketplaceSuppliers(): Promise<{ name: string; n: number }[]> {
  const { data, error } = await supabase.rpc('fn_dropi_marketplace_suppliers');
  if (error) throw error;
  return ((data ?? []) as { supplier: string; n: number }[]).map((r) => ({ name: r.supplier, n: Number(r.n) || 0 }));
}

export interface DropiPickedItem {
  dropi_product_id: number;
  variation_id?: number | null;
  name: string;
  sku?: string | null;
  image?: string | null;
  suggested_price?: number | null;
  cost_price?: number | null;
  stock?: number | null;
  supplier_id?: number | null;
}

/**
 * Importa un producto del catálogo de Dropi al catálogo de Smarty (modelo dropshipping) y deja
 * el vínculo en `dropi_product_map`. Idempotente por (dropi_product_id, variation_id): si ya se
 * importó, reutiliza el producto existente en vez de duplicarlo. Copia la foto del proveedor a R2.
 * Devuelve el product_id para agregarlo como línea de pedido.
 */
export async function createProductFromDropi(tenantId: string, item: DropiPickedItem): Promise<string> {
  const { productId } = await importFromSupplier(tenantId, 'dropi', item);
  return productId;
}

/** Igual que `createProductFromDropi`, pero devolviendo también qué pasó con la foto. */
export function importProductFromDropi(
  tenantId: string,
  item: DropiPickedItem,
): Promise<SupplierImportResult> {
  return importFromSupplier(tenantId, 'dropi', item);
}

/**
 * Registra el pedido en Dropi (edge fn `dropi-register`). El edge fn ya valida stock en vivo,
 * es idempotente en dos capas (local + shop_order_id) y persiste order_shipments; acá solo
 * devolvemos el id para el feedback. Dropi cotiza y elige transportadora por su cuenta.
 */
export async function registerShipmentDropi(order: OrderRecord): Promise<{ dropiOrderId?: string; already?: boolean }> {
  const { data, error } = await supabase.functions.invoke('dropi-register', {
    body: { tenant_id: order.tenant_id, order_id: order.id },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string; detail?: string; unmapped?: string[]; dropi_order_id?: string; already?: boolean };
  const extra = res.detail ? ` · ${res.detail}` : res.unmapped?.length ? ` (${res.unmapped.join(', ')})` : '';
  if (error) throw new Error((res?.error || await errorDeEdgeFunction(error, 'No se pudo registrar en Dropi.')) + extra);
  if (!res.ok) throw new Error((res?.error || 'No se pudo registrar en Dropi.') + extra);
  return { dropiOrderId: res.dropi_order_id, already: res.already };
}

/** Consulta y sincroniza el estado del pedido en Dropi (sin webhook: es pull). */
export async function trackShipmentDropi(
  order: OrderRecord,
): Promise<{ status: string; dropiStatus?: string | null; guideNumber?: string | null; shippingCompany?: string | null; raw: Record<string, unknown> }> {
  const { data, error } = await supabase.functions.invoke('dropi-track', {
    body: { tenant_id: order.tenant_id, order_id: order.id },
  });
  const res = data as {
    ok?: boolean; status?: string; dropi_status?: string | null; guide_number?: string | null;
    shipping_company?: string | null; raw?: Record<string, unknown>; error?: string;
  } | null;
  if (error) throw new Error(res?.error || await errorDeEdgeFunction(error, 'No se pudo consultar el estado en Dropi.'));
  if (!res?.ok) throw new Error(res?.error ?? 'Dropi no devolvió estado para este pedido.');
  return {
    status: res.status ?? '',
    dropiStatus: res.dropi_status,
    guideNumber: res.guide_number,
    shippingCompany: res.shipping_company,
    raw: res.raw ?? {},
  };
}

/** Estado de conexión de Dropi (edge fn `dropi-connect` con check). */
export async function checkDropiConnection(
  tenantId: string,
): Promise<{ connected: boolean; country: string; countries: string[]; lastValidatedAt: string | null }> {
  // Estado vía RPC (PostgREST, siempre caliente) en vez de una edge function que arranca en frío
  // ~1-2s. La lista de países disponibles es estática (no depende de la conexión).
  const { data, error } = await supabase.rpc('fn_courier_connection_status', { p_tenant: tenantId, p_courier: 'dropi' });
  if (error) throw new Error(await errorDeEdgeFunction(error, 'No se pudo consultar la conexión con Dropi.'));
  const row = ((data ?? []) as { connected?: boolean; country?: string; last_validated_at?: string | null }[])[0];
  return {
    connected: row?.connected === true,
    country: row?.country ?? 'PE',
    countries: Object.keys(DROPI_COUNTRY_LABELS),
    lastValidatedAt: row?.last_validated_at ?? null,
  };
}

/** Estado de conexión de Aliclik (RPC warm, sin cold-start del edge function). */
export async function checkAliclikConnection(tenantId: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('fn_courier_connection_status', { p_tenant: tenantId, p_courier: 'aliclik' });
  if (error) return false;
  const row = ((data ?? []) as { connected?: boolean }[])[0];
  return row?.connected === true;
}

/**
 * Detalle de la conexión Aliclik (api_base, canal y webhook_token) vía `aliclik-connect` (check).
 * Se usa para mostrar/editar la URL de la API (dev vs producción) y la URL de webhook a entregar
 * a Aliclik. Cold-start del edge fn (~1-2s); tolerable en la pantalla de ajustes.
 */
export async function getAliclikConnectionDetails(
  tenantId: string,
): Promise<{ connected: boolean; apiBase: string; channel: string; webhookToken: string | null }> {
  const { data, error } = await supabase.functions.invoke('aliclik-connect', { body: { tenant_id: tenantId, check: true } });
  if (error) return { connected: false, apiBase: '', channel: 'SMARTY', webhookToken: null };
  const res = (data ?? {}) as { connected?: boolean; api_base?: string; channel?: string; webhook_token?: string | null };
  return {
    connected: res.connected === true,
    apiBase: res.api_base ?? '',
    channel: res.channel || 'SMARTY',
    webhookToken: res.webhook_token ?? null,
  };
}

/** Conecta Dropi validando el token contra la API del país elegido. */
export async function connectDropi(tenantId: string, token: string, country: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('dropi-connect', {
    body: { tenant_id: tenantId, token, country },
  });
  const res = data as { ok?: boolean; error?: string; detail?: string } | null;
  const extra = res?.detail ? ` · ${res.detail}` : '';
  if (error) throw new Error((res?.error || error.message || 'No se pudo conectar con Dropi.') + extra);
  if (!res?.ok) throw new Error((res?.error ?? 'No se pudo conectar con Dropi.') + extra);
}

/** Desconecta Dropi (borra la credencial cifrada del tenant). */
export async function disconnectDropi(tenantId: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('dropi-connect', { body: { tenant_id: tenantId, disconnect: true } });
  const res = data as { ok?: boolean; error?: string } | null;
  if (error) throw new Error(res?.error || error.message || 'No se pudo desconectar Dropi.');
  if (!res?.ok) throw new Error(res?.error ?? 'No se pudo desconectar Dropi.');
}

/**
 * Registra el envío en Eva Courier en tiempo real (edge fn `eva-register`, adaptador de la API
 * oficial). Eva es entrega a domicilio (motorizado): mapea el pedido (dirección/distrito del perfil
 * COD + monto COD) y devuelve la guía (tracking_id). El edge fn YA persiste order_shipments
 * (idempotente); acá solo devolvemos la guía para el feedback. Eva liquida el COD al comercio.
 */
export async function registerShipmentEva(
  order: OrderRecord,
  opts?: { paymentMethod?: string; serviceType?: number; clientType?: 'RECOJO' | 'ALMACEN' },
): Promise<{ trackingId?: string; already?: boolean }> {
  const { data, error } = await supabase.functions.invoke('eva-register', {
    body: {
      tenant_id: order.tenant_id,
      order_id: order.id,
      payment_method: opts?.paymentMethod,
      service_type: opts?.serviceType,
      client_type: opts?.clientType,
    },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string; detail?: string; tracking_id?: string; already?: boolean };
  const msg = (m: string) => (res.detail ? `${m} · ${res.detail}` : m);
  if (error) throw new Error(msg(res?.error || error.message || 'No se pudo registrar en Eva.'));
  if (!res.ok) throw new Error(msg(res?.error || 'No se pudo registrar en Eva.'));
  return { trackingId: res.tracking_id, already: res.already };
}

/** Consulta el estado del envío en Eva (reusa el edge fn `eva-track`). Devuelve el estado
 *  normalizado + el payload crudo (estado actual + timeline) para mostrarlo en el modal. */
export async function trackShipmentEva(
  order: OrderRecord,
  input: { trackingId?: string | null },
): Promise<{ estado: string; status: string; raw: Record<string, unknown>; deleted?: boolean }> {
  const { data, error } = await supabase.functions.invoke('eva-track', {
    body: { tenant_id: order.tenant_id, order_id: order.id, tracking_id: input.trackingId ?? '' },
  });
  const res = data as { ok?: boolean; estado?: string; status?: string; raw?: Record<string, unknown>; error?: string; deleted?: boolean } | null;
  if (error) throw new Error(error.message ?? 'No se pudo consultar el estado en Eva.');
  if (!res?.ok) throw new Error(res?.error ?? 'Eva no devolvió estado para este envío.');
  // deleted = la guía ya no existe en Eva (se eliminó en su panel).
  if (res.deleted) return { estado: '', status: 'deleted', raw: {}, deleted: true };
  return { estado: res.estado ?? '', status: res.status ?? '', raw: res.raw ?? {} };
}

/** Marca el estado de envío de varios pedidos a la vez (acción masiva de Logística). */
export async function bulkSetShipping(tenantId: string, orderIds: string[], status: string): Promise<void> {
  if (orderIds.length === 0) return;
  const { error } = await supabase
    .from('orders_v2')
    .update({ shipping_status: status })
    .in('id', orderIds)
    .eq('tenant_id', tenantId);
  if (error) throw error;
}

/**
 * Prepara el envío de un pedido de Falabella y trae la etiqueta.
 *
 * EN FALABELLA EL TRANSPORTISTA ES FALABELLA: el vendedor no elige courier ni marca "enviado" — arma
 * el paquete, pega la etiqueta y lo lleva al punto autorizado. Esto hace los tres pasos de su API
 * (empaquetar → listo para despachar → etiqueta) y devuelve el PDF listo para imprimir.
 */
export async function fulfillFalabellaOrder(
  order: OrderRecord,
): Promise<{ prepared: string[]; packageId: string | null; trackingNumber: string | null; labelBase64: string; mimeType: string }> {
  const { data, error } = await supabase.functions.invoke('falabella-fulfill', {
    body: { tenant_id: order.tenant_id, order_id: order.id },
  });
  const res = (data ?? {}) as {
    ok?: boolean; error?: string; prepared?: string[]; package_id?: string | null;
    tracking_number?: string | null; label?: { mime_type?: string; base64?: string };
  };
  if (error || !res.ok) throw new Error(res.error || error?.message || 'No se pudo preparar el envío en Falabella.');

  return {
    prepared: res.prepared ?? [],
    packageId: res.package_id ?? null,
    trackingNumber: res.tracking_number ?? null,
    labelBase64: res.label?.base64 ?? '',
    mimeType: res.label?.mime_type ?? 'application/pdf',
  };
}

/**
 * El RÓTULO del marketplace (Falabella o Mercado Libre): el PDF con SU código de barras.
 *
 * Es otro papel que el rótulo de Smarty. El de Smarty sirve para los envíos propios; éste es el
 * único que aceptan en el punto de despacho del marketplace — sin él no te reciben el paquete.
 */
export async function printMarketplaceLabel(
  order: OrderRecord,
): Promise<{ marketplace: string; base64: string; mimeType: string }> {
  const { data, error } = await supabase.functions.invoke('marketplace-shipping', {
    body: { action: 'label', tenant_id: order.tenant_id, order_id: order.id },
  });
  const res = (data ?? {}) as {
    ok?: boolean; error?: string; marketplace?: string; label?: { mime_type?: string; base64?: string };
  };
  if (error || !res.ok) throw new Error(await mensajeDeLaFuncion(error, res, 'No se pudo traer el rótulo.'));

  return {
    marketplace: res.marketplace ?? 'Marketplace',
    base64: res.label?.base64 ?? '',
    mimeType: res.label?.mime_type ?? 'application/pdf',
  };
}

/**
 * El PUNTO DE DESPACHO del vendedor ("Falabella San Miguel", "Urbano de Pueblo Libre").
 *
 * 🪤 Ninguna API lo dice: cuál punto autorizado elegiste se decide dentro del panel del marketplace y
 * no lo exponen. Es un dato del VENDEDOR, no del pedido — se declara una vez y vale para todos sus
 * envíos de ese marketplace.
 *
 * 🪤 Y NO se guarda desde el navegador: vive en la cuenta del marketplace, junto a las CREDENCIALES,
 * en tablas cerradas por RLS. Abrirlas al cliente para guardar un texto sería regalar los tokens.
 */
export async function saveMarketplaceDropoff(order: OrderRecord, dropoffPoint: string): Promise<void> {
  const { data, error } = await supabase.functions.invoke('marketplace-shipping', {
    body: {
      action: 'set_dropoff',
      tenant_id: order.tenant_id,
      order_id: order.id,
      dropoff_point: dropoffPoint,
    },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string };
  if (error || !res.ok) throw new Error(await mensajeDeLaFuncion(error, res, 'No se pudo guardar el punto.'));
}

/** El punto de despacho declarado para ese marketplace (o null si el vendedor no lo cargó todavía). */
export async function loadMarketplaceDropoff(order: OrderRecord): Promise<string | null> {
  const { data } = await supabase.functions.invoke('marketplace-shipping', {
    body: { action: 'get_dropoff', tenant_id: order.tenant_id, order_id: order.id },
  });
  return ((data ?? {}) as { dropoff_point?: string | null }).dropoff_point ?? null;
}

/**
 * Adjunta al marketplace el comprobante YA EMITIDO (boleta/factura) del pedido.
 *
 * Emitir es una acción fiscal aparte y deliberada; esto solo le pasa a Falabella el link del PDF que
 * el comprador pidió. Requiere que el comprobante esté aceptado por SUNAT y tenga PDF.
 */
export async function attachMarketplaceInvoice(
  order: OrderRecord,
): Promise<{ marketplace: string; invoiceNumber: string }> {
  const { data, error } = await supabase.functions.invoke('marketplace-shipping', {
    body: { action: 'attach_invoice', tenant_id: order.tenant_id, order_id: order.id },
  });
  const res = (data ?? {}) as { ok?: boolean; error?: string; marketplace?: string; invoice_number?: string };
  if (error || !res.ok) throw new Error(await mensajeDeLaFuncion(error, res, 'No se pudo adjuntar el comprobante.'));
  return { marketplace: res.marketplace ?? 'Marketplace', invoiceNumber: res.invoice_number ?? '' };
}

// ── Embudo de confirmación (ADR 0029 F7) ────────────────────────────────────────────────────────

/**
 * De los pedidos que entraron, cuántos llegaron a ser una venta.
 *
 * Hasta el ADR 0029 esta pregunta no se podía hacer: «confirmado» había que deducirlo enumerando
 * `['sale','fulfilled','closed']` y el momento en que cruzó esa línea no se guardaba.
 */
export interface EmbudoConfirmacion {
  creados: number;
  confirmados: number;
  pendientes: number;
  perdidos: number;
  tasa: number;
  /** Confirmaciones que el sistema VIO ocurrir. Solo estas alimentan la mediana. */
  observados: number;
  /** Confirmaciones deducidas del histórico (`confirmation_mode = 'backfill'`). */
  reconstruidos: number;
  /** Mediana de horas hasta confirmar. `null` mientras no haya confirmaciones observadas. */
  horas_mediana: number | null;
}

export async function fetchEmbudoConfirmacion(tenantId: string, dias: number): Promise<EmbudoConfirmacion> {
  const { data, error } = await supabase.rpc('fn_embudo_de_confirmacion', {
    p_tenant: tenantId,
    p_dias: dias,
  });
  if (error) throw error;
  const fila = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined;
  return {
    creados: Number(fila?.creados ?? 0),
    confirmados: Number(fila?.confirmados ?? 0),
    pendientes: Number(fila?.pendientes ?? 0),
    perdidos: Number(fila?.perdidos ?? 0),
    tasa: Number(fila?.tasa ?? 0),
    observados: Number(fila?.observados ?? 0),
    reconstruidos: Number(fila?.reconstruidos ?? 0),
    // 🪤 `null` y no 0: «todavía no se puede medir» y «se mide y da cero» son cosas distintas, y
    //    la pantalla tiene que poder decirlas distinto.
    horas_mediana: fila?.horas_mediana == null ? null : Number(fila.horas_mediana),
  };
}

/**
 * De dónde viene cada producto: `propio`, `aliclik` o `dropi`.
 *
 * Sale de `v_producto_abastecimiento`, que lo DERIVA del vínculo con el proveedor y no del tag del
 * producto (un tag lo edita cualquiera; el vínculo es lo que permite despachar). Se pide por lote de
 * ids —lo que hay en pantalla— en vez de traer el catálogo entero: son ≤50 filas por consulta.
 */
export async function fetchAbastecimientoDeProductos(
  tenantId: string,
  productIds: string[],
): Promise<Map<string, string>> {
  const ids = [...new Set(productIds.filter(Boolean))];
  if (ids.length === 0) return new Map();
  const { data, error } = await supabase
    .from('v_producto_abastecimiento')
    .select('product_id, abastecimiento')
    .eq('tenant_id', tenantId)
    .in('product_id', ids);
  if (error) throw error;
  return new Map((data ?? []).map((r) => [(r as { product_id: string }).product_id, (r as { abastecimiento: string }).abastecimiento]));
}


/** Lo que compró un cliente, resumido para la columna del chat. */
export interface ResumenDeCompras {
  total: number;
  pedidos: number;
  ultimaISO: string | null;
}

/**
 * Cuánto compró de verdad este contacto: total, cantidad y fecha de la última.
 *
 * ── POR QUÉ VIVE ACÁ Y NO EN EL COMPONENTE ────────────────────────────────────────────────────
 * 🚨 LO EXIGIÓ EL GUARDIA DEL ADR-0029, y tenía razón. La consulta nació dentro de
 * `WaFichaDelCliente`, o sea una query suelta contra `orders_v2` desde la columna de un chat. El
 * gate «Accesos directos al dominio Pedidos» la marcó con el motivo escrito: el pedido está
 * cambiando de modelo, y una consulta contra las columnas viejas es la que va a quedar mintiendo
 * cuando se retire el enum de etapa que el ADR-0029 retira en F6. Desde acá se migra en un solo lugar.
 *
 * 🪤 EL COMENTARIO NO ESCRIBE EL NOMBRE DEL ENUM: `check-enum-etapa` cuenta las menciones sobre
 * el archivo crudo, comentarios incluidos. Explicar por qué se retira, nombrándolo, hacía subir el
 * contador que este cambio vino a bajar.
 *
 * 🚨 FILTRA CON `esVenta`, LA DEFINICIÓN DEL REPO. Un presupuesto o un pedido cancelado no son
 * plata comprada. La primera vez que alguien escribió su propia lista de estados, una ficha mostró
 * S/419.063 de deuda que no existía.
 *
 * 🪤 EL FILTRO VA EN TYPESCRIPT Y NO EN SQL, a propósito: `esVenta` es UNA función y se usa igual
 * en la ficha del cliente, en la columna «Tipo» y acá. Escribir la lista de estados en el `where`
 * sería la quinta definición de «venta» del repo, y la que nadie actualiza cuando cambia el enum.
 */
export async function fetchResumenDeCompras(
  tenantId: string,
  contactId: string,
): Promise<ResumenDeCompras> {
  const { data } = await supabase
    .from('orders_v2')
    .select('grand_total, status, created_at')
    .eq('tenant_id', tenantId)
    .eq('contact_id', contactId)
    .order('created_at', { ascending: false })
    .limit(200);

  return resumirCompras((data ?? []) as FilaDeCompra[]);
}

/** Una fila de `orders_v2`, con lo mínimo para saber si contó y cuánto. */
export interface FilaDeCompra {
  grand_total: number | null;
  status: string | null;
  created_at: string;
}

/**
 * La parte que decide: qué cuenta como compra y cuánto suma.
 *
 * Está separada de la consulta para poder probarla sin base ni red. El caso que la justifica es el
 * del presupuesto de S/4.000 que no debe sumar.
 *
 * 🪤 EL MÁXIMO SE CALCULA ACÁ y no se confía en el `order by` de la consulta: si mañana alguien
 * agrega un filtro que reordena, «último» empezaría a mostrar una fecha vieja sin que nada falle.
 */
export function resumirCompras(filas: FilaDeCompra[]): ResumenDeCompras {
  const ventas = filas.filter((o) => esVenta(o.status));
  return {
    total: ventas.reduce((s, o) => s + Number(o.grand_total || 0), 0),
    pedidos: ventas.length,
    ultimaISO: ventas.reduce<string | null>(
      (max, o) => (!max || o.created_at > max ? o.created_at : max),
      null,
    ),
  };
}

/**
 * La dirección de una agencia de destino, por su nombre.
 *
 * 🚨 PARA QUE EL RÓTULO DIGA A DÓNDE VA EL PAQUETE. El founder: «cuando al cliente se le manda por
 *    agencia y no tiene dirección registrada, ¿debería figurar la dirección de Shalom en el rótulo?».
 *    Sí — y no como respaldo: en recojo por agencia el paquete NO va a la casa del cliente, va al
 *    terminal, así que ESA es la dirección de entrega. Amazon Counter, FedEx Hold at Location y
 *    Mercado Libre retiro en punto imprimen exactamente eso.
 *
 * 🪤 SE BUSCA POR NOMBRE, y no es la llave que uno querría. `order_delivery_profile.shalom_agency_id`
 *    existe pero está en NULL en los 352 envíos por agencia de la base: nunca se llenó. El nombre
 *    resuelve 305 de esos 352 (87 %), y el resto son agencias de otros couriers o variantes de
 *    nombre. Cuando no resuelve se imprime sólo el nombre — nunca una dirección inventada.
 *
 * 🪤 NO LANZA. Un rótulo tiene que poder imprimirse aunque el catálogo no conteste: sin dirección
 *    sigue siendo un rótulo válido, y frenar la impresión por un dato de adorno sería peor.
 */
export async function fetchAgencyAddress(agencyName: string | null | undefined): Promise<string | null> {
  const nombre = (agencyName ?? '').trim();
  if (!nombre) return null;
  try {
    const { data } = await supabase
      .from('shalom_agencies')
      .select('address')
      .ilike('name', nombre)
      .limit(1)
      .maybeSingle();
    return ((data as { address?: string | null } | null)?.address ?? '').trim() || null;
  } catch {
    return null;
  }
}
